<script lang="ts">
  import { onMount, onDestroy, afterUpdate } from "svelte"
  import { browser } from "$app/environment"
  import * as THREE from "three"
  import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js"
  import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js"
  import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js"
  import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js"

  // Add explicit container reference
  let container: HTMLElement | null = null

  // Improved debug configuration to control logging frequency
  const debug = {
    enabled: false, // Set to false to disable debug logs
    verbose: false, // Set to true for verbose logging
    connectionLogs: false, // Log connection creation details
    activationLogs: false, // Log neuron activations
    pulseLogs: false, // Log pulse creation
    logFrequency: 0.01, // Frequency for throttled logs (0.01 = 1%)
    lastNetworkStatusTime: 0, // Throttle network status logs
    statusLogInterval: 5000, // Log network status every 5 seconds
    // New setting to control even forced logs
    showCriticalLogs: false, // Only show truly critical logs when true
    // Categories that are considered critical even when logs are disabled
    criticalCategories: ["error", "fatal"],
  }

  // Improved debug log helper to reduce console noise
  function debugLog(category: string, message: string, force = false) {
    // Skip all logs if not in development mode
    if (import.meta.env.MODE !== "development") return

    // Only show forced logs if they're critical or showCriticalLogs is enabled
    if (
      force &&
      !debug.showCriticalLogs &&
      !debug.criticalCategories.includes(category)
    ) {
      return
    }

    // Skip non-forced logs if debugging is disabled
    if (!debug.enabled && !force) return

    // Apply throttling based on category
    if (!force) {
      if (category === "activation" && !debug.activationLogs) return
      if (category === "pulse" && !debug.pulseLogs) return
      if (category === "connection" && !debug.connectionLogs) return

      // Apply frequency throttling for common events
      if (
        ["activation", "pulse", "interaction"].includes(category) &&
        Math.random() > debug.logFrequency
      )
        return
    }

    console.log(`[${category}] ${message}`)
  }

  let scene: THREE.Scene
  let camera: THREE.PerspectiveCamera
  let renderer: THREE.WebGLRenderer
  let particles: THREE.Points
  let axons: THREE.LineSegments // Neural connections
  let activations: THREE.Points // Neural activation pulses
  let neuronSpheres: THREE.InstancedMesh // Actual neuron spheres to connect with
  let animationId: number
  let clock: THREE.Clock
  let uniforms: any
  let activationUniforms: any
  let axonPositions: Float32Array // Store axon endpoint positions
  let neuronIndices: Int32Array // Store which neurons are connected
  let neuronPositions: Float32Array // Store current neuron positions
  let composer: EffectComposer

  // Enhanced parameters with more subtle, elegant visual effects
  const params = {
    backgroundColor: 0x030810,
    particleCount: 100, // Reduced from 120 for more elegant spacing
    axonCount: 150, // Fewer connections for cleaner look
    activationCount: 100, // Fewer simultaneous activations
    neuronColor: new THREE.Color(0x2a4e99), // More subtle, deeper blue
    neuronGlowColor: new THREE.Color(0x4a70c8), // Less vibrant glow
    pulseColor: new THREE.Color(0x8aa5e0), // Softer pulse color
    activationColor: new THREE.Color(0x6085d0), // More elegant activation color
    neuronSize: 1.5, // Smaller neurons (was 2.0)
    neuronGlowSize: 2.0, // Smaller glow (was 2.5)
    activationSize: 1.8, // Smaller activation points (was 2.2)
    neuronSpread: { x: 90, y: 60, z: 75 }, // More Z depth
    bloomStrength: 0.6, // Reduced bloom for subtlety (was 0.8)
    bloomRadius: 0.55, // Smaller bloom radius (was 0.65)
    bloomThreshold: 0.15, // Higher threshold for more selective bloom (was 0.1)
    usePostProcessing: true,
    activationPulseStrength: 1.8, // More subtle pulse (was 2.5)
    activationPropagationChance: 0.95, // Increase from 0.85 for more reliable propagation
    activationDecayRate: 0.96, // Slower decay (was 0.95) to allow more propagation time
    activationMaxBranches: 4, // Fewer branches for cleaner paths (was 6)
    branchingDelay: 0.02, // Slightly slower branching for more elegance
    neuronActivationSize: 3.0, // Size boost when neuron activates
    axonStrengthDecayRate: 0.99997, // Very slow decay for inactive axons
    axonStrengthGrowthRate: 1.25, // Boosted from 1.15 for faster strengthening
    axonMinStrength: 0.15, // Reduced from 0.2 to keep connections visible longer
    axonDeathThreshold: 0.05, // Axons below this completely die
    neuronDeathThreshold: 0.1, // Neurons below this value start to fade
    neuronRegenerationRate: 0.0002, // Chance per frame to regenerate connections
    preferStrongerConnections: 0.8, // How much to favor stronger connections during activation
    regenerateConnections: true, // Whether to create new connections when old ones die
    axonPulseWidth: 60.0, // Increase from 30.0 for more visible pulses
    axonPulseIntensity: 2.5, // Increase from 1.2 for brighter pulses
    activationSpeed: 0.14, // Decrease from 0.18 for slower, more visible propagation
    axonRadius: 0.2, // Define the radius of axon cylinders for visualization
    axonColor: new THREE.Color(0x3e68ff), // Add explicit axonColor for materials
  }

  // New tracking system for activations
  let activeNeurons = new Map() // Track which neurons are currently active
  let pendingActivations = [] // Queue of upcoming activations
  let lastActivationTime = 0 // Track timing for controlled activation frequency

  // Track axon strengths for organic connection patterns
  let axonStrengths = new Float32Array(params.axonCount).fill(1.0)
  let neuronStrengths = new Float32Array(params.particleCount).fill(1.0)
  let deadAxons = new Set() // Track which axons are considered "dead"
  let lastUsedTime = new Float32Array(params.axonCount).fill(0) // Track when axons were last used

  // Color correction shader for enhanced visuals
  const ColorCorrectionShader = {
    uniforms: {
      tDiffuse: { value: null },
      powRGB: { value: new THREE.Vector3(1.1, 1.1, 1.2) },
      mulRGB: { value: new THREE.Vector3(1.2, 1.1, 1.3) },
      addRGB: { value: new THREE.Vector3(0.0, 0.02, 0.1) },
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D tDiffuse;
      uniform vec3 powRGB;
      uniform vec3 mulRGB;
      uniform vec3 addRGB;
      varying vec2 vUv;

      void main() {
        gl_FragColor = texture2D(tDiffuse, vUv);
        gl_FragColor.rgb = mulRGB * pow(gl_FragColor.rgb, powRGB) + addRGB;
      }
    `,
  }

  // Vignette shader for edge darkening
  const VignetteShader = {
    uniforms: {
      tDiffuse: { value: null },
      offset: { value: 0.95 },
      darkness: { value: 1.6 },
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D tDiffuse;
      uniform float offset;
      uniform float darkness;
      varying vec2 vUv;

      void main() {
        vec4 texel = texture2D(tDiffuse, vUv);
        vec2 uv = (vUv - 0.5) * 2.0;
        float vignetteAmount = 1.0 - dot(uv, uv) * offset;
        vignetteAmount = pow(vignetteAmount, darkness);
        texel.rgb *= vignetteAmount;
        gl_FragColor = texel;
      }
    `,
  }

  // Helper function to detect mobile/low-end devices
  function isMobileDevice() {
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      ) ||
      window.innerWidth < 768 ||
      window.devicePixelRatio > 2
    )
  }

  // Define onWindowResize function before it's used in initScene
  function onWindowResize() {
    camera.aspect = window.innerWidth / window.innerHeight
    camera.updateProjectionMatrix()
    renderer.setSize(window.innerWidth, window.innerHeight)

    // Also resize composer if it exists
    if (composer && params.usePostProcessing) {
      composer.setSize(window.innerWidth, window.innerHeight)
    }

    // Disable some effects on smaller screens for performance
    if (window.innerWidth < 768 && params.usePostProcessing) {
      params.bloomStrength = 0.4 // Reduce bloom intensity on small screens
    }
  }

  // Add animation startup control parameters
  let startupPhase = 0 // 0: initial fade in, 1: structural elements visible, 2: begin activations, 3: full activity
  let startTime = 0
  let fadeInDuration = 5.0 // 5 seconds total fade in before activations begin
  let fullStrengthTime = 10.0 // 10 seconds until full visual intensity

  // Add startup intensity control - early neurons start dimmer
  let startupIntensityMultiplier = 0.2 // Start at just 20% intensity

  // Add mouse interaction variables
  let mousePosition = new THREE.Vector2(-1000, -1000) // Start off-screen
  let targetMousePosition = new THREE.Vector2(-1000, -1000) // Target position for smooth interpolation
  let raycaster = new THREE.Raycaster()
  let intersectedNeuron = null
  let lastMouseMoveTime = 0
  let mouseMoveCooldown = 16 // Reduced from 50 for much smoother interaction (about 60fps)
  let mouseActivationRadius = 80 // Doubled from 40 for easier interaction
  let mouseHoverIntensity = 3.0 // Increased from 2.0 for more visible effect
  let mouseActivationEnabled = true // Can be toggled for performance
  let mouseDebugSphere = null // For visual debugging
  let currentHoveredNeuron = null // Track the currently hovered neuron
  let hoverStartTime = 0 // When the hover started
  let hoverTransitionDuration = 300 // ms to smoothly increase intensity
  let hoverEndTransitionDuration = 800 // ms to smoothly fade out hover effect

  // Parameters for more interesting activation patterns
  const activationPatterns = [
    {
      name: "ripple",
      chance: 0.3,
      minConnections: 4,
      maxConnections: 8,
      intensityFactor: 1.2,
    },
    {
      name: "cascade",
      chance: 0.25,
      minConnections: 3,
      maxConnections: 5,
      intensityFactor: 1.4,
    },
    {
      name: "pulse",
      chance: 0.2,
      minConnections: 1,
      maxConnections: 3,
      intensityFactor: 1.6,
    },
    {
      name: "burst",
      chance: 0.15,
      minConnections: 5,
      maxConnections: 10,
      intensityFactor: 1.1,
    },
    {
      name: "sparkle",
      chance: 0.1,
      minConnections: 2,
      maxConnections: 4,
      intensityFactor: 1.3,
    },
  ]

  function initScene() {
    scene = new THREE.Scene()

    // Enhanced camera settings with more dramatic angle
    camera = new THREE.PerspectiveCamera(
      68, // Wider FOV for more dramatic perspective
      window.innerWidth / window.innerHeight,
      1,
      1000,
    )
    camera.position.z = 130
    camera.position.y = 25
    camera.position.x = -15

    // Renderer setup with precision selection for performance
    renderer = new THREE.WebGLRenderer({
      antialias: window.devicePixelRatio < 2, // Only use antialiasing on lower pixel ratio devices
      alpha: true,
      powerPreference: "high-performance",
    })
    renderer.setSize(window.innerWidth, window.innerHeight)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)) // Limit pixel ratio
    renderer.setClearColor(params.backgroundColor, 1)
    renderer.sortObjects = true // Enable object sorting for proper transparency handling

    // Enable tone mapping for better color rendering
    renderer.toneMapping = THREE.ACESFilmicToneMapping
    renderer.toneMappingExposure = 1.0

    container.appendChild(renderer.domElement)

    // Create improved lighting for depth
    const ambientLight = new THREE.AmbientLight(0x202060, 0.2)
    scene.add(ambientLight)

    // Add subtle directional light for better 3D appearance
    const directionalLight = new THREE.DirectionalLight(0x4060ff, 0.3)
    directionalLight.position.set(0.5, 1, 0.5)
    scene.add(directionalLight)

    // Create neural network elements with enhanced visuals
    createNeurons()
    createNeuronGlows() // New function to add glowing auras
    createNeuronSpheres()
    createAxons()
    createActivations()

    // Setup post-processing with enhanced effects
    setupPostProcessing()

    // Event listeners
    window.addEventListener("resize", onWindowResize)
  }

  function setupPostProcessing() {
    // Skip for very low-end devices or when disabled
    if (!params.usePostProcessing) {
      return
    }

    // Create effect composer
    composer = new EffectComposer(renderer)

    // Add standard render pass
    const renderPass = new RenderPass(scene, camera)
    composer.addPass(renderPass)

    // Add optimized bloom pass for the glow effect
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      params.bloomStrength,
      params.bloomRadius,
      params.bloomThreshold,
    )
    composer.addPass(bloomPass)

    // Add color correction for enhanced visual appeal
    const colorCorrectionPass = new ShaderPass(ColorCorrectionShader)
    composer.addPass(colorCorrectionPass)

    // Add vignette effect for edge darkening
    const vignettePass = new ShaderPass(VignetteShader)
    composer.addPass(vignettePass)
  }

  function createNeurons() {
    // Create particles for neurons but make them static (no animation)
    const geometry = new THREE.BufferGeometry()
    const positions = new Float32Array(params.particleCount * 3)
    const scales = new Float32Array(params.particleCount)

    // Store positions for later use
    neuronPositions = positions

    // Create neuron positions with better distribution
    for (let i = 0; i < params.particleCount; i++) {
      // More controlled distribution - avoid center clumping
      const phi = Math.acos(2 * Math.random() - 1)
      const theta = Math.random() * Math.PI * 2

      const radius = 20 + Math.pow(Math.random(), 0.5) * 60 // Better radial distribution

      positions[i * 3] =
        (radius * Math.sin(phi) * Math.cos(theta) * params.neuronSpread.x) / 60
      positions[i * 3 + 1] =
        (radius * Math.sin(phi) * Math.sin(theta) * params.neuronSpread.y) / 60
      positions[i * 3 + 2] =
        (radius * Math.cos(phi) * params.neuronSpread.z) / 60

      // Random sizes for depth effect
      scales[i] = Math.random() * 1.2 + 0.7
    }

    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute("scale", new THREE.BufferAttribute(scales, 1))

    // Neuron shader uniforms with activation and strength support
    uniforms = {
      time: { value: 0.0 },
      color: { value: params.neuronColor },
      pointSize: { value: params.neuronSize },
      activeNeurons: { value: new Float32Array(params.particleCount).fill(0) },
      neuronStrengths: {
        value: new Float32Array(params.particleCount).fill(1.0),
      },
    }

    // Enhanced shader for neurons that considers both activation and strength
    const material = new THREE.ShaderMaterial({
      uniforms: uniforms,
      vertexShader: `
        uniform float time;
        uniform float pointSize;
        uniform float activeNeurons[${params.particleCount}];
        uniform float neuronStrengths[${params.particleCount}];
        
        attribute float scale;
        varying vec3 vPosition;
        varying float vActivation;
        varying float vStrength;
        
        void main() {
          vPosition = position;
          
          // Get neuron activation and strength states
          float activation = activeNeurons[gl_VertexID];
          float strength = neuronStrengths[gl_VertexID];
          
          vActivation = activation;
          vStrength = strength;
          
          // Keep neurons in fixed positions - REMOVE the movement animation
          // that creates the appearance of orbiting particles
          vec3 pos = position;
          
          // Apply position directly without animation
          vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
          gl_Position = projectionMatrix * mvPosition;
          
          // Size increases with activation but decreases with low strength
          float strengthFactor = smoothstep(0.1, 0.5, strength); // Fade out when below threshold
          float activationBoost = 1.0 + activation * 3.0;
          float pulse = 1.0 + 0.2 * sin(time + scale * 10.0);
          
          // Enhanced pulsing effect when activated - FIX: use position hash instead of gl_VertexID
          if (activation > 0.0) {
            // Create unique value from position to use instead of gl_VertexID
            float uniqueVal = fract(position.x * 34.671 + position.y * 19.827 + position.z * 7.283) * 100.0;
            pulse = 1.0 + 0.5 * sin(time * 4.0 + uniqueVal);
          }
          
          float size = pointSize * scale * pulse * activationBoost * strengthFactor;
          gl_PointSize = size * (300.0 / -mvPosition.z);
        }
      `,
      fragmentShader: `
        uniform vec3 color;
        varying vec3 vPosition;
        varying float vActivation;
        varying float vStrength;
        
        void main() {
          // Create a glowing point with core and soft edge
          vec2 xy = gl_PointCoord.xy - vec2(0.5);
          float radius = length(xy);
          
          // Two-step gradient for more interesting appearance
          float core = 1.0 - smoothstep(0.1, 0.5, radius); 
          float edge = 1.0 - smoothstep(0.4, 0.5, radius);
          
          // Combine for a more interesting glow effect - reduced intensity
          float alpha = max(core * 0.7, edge * 0.4); // Reduced from 0.8/0.6
          
          // Scale alpha by strength to fade out dying neurons
          alpha *= smoothstep(0.1, 0.3, vStrength);
          
          // Enhanced color variation based on activation and strength
          vec3 baseColor = color + vPosition.z * 0.003;
          vec3 activeColor = vec3(0.9, 0.95, 1.0); // Slightly less white when activated
          vec3 weakColor = vec3(0.5, 0.0, 0.0);   // Reddish when dying
          
          // Mix colors based on activation and strength with reduced activation impact
          vec3 strengthAdjustedColor = mix(weakColor, baseColor, smoothstep(0.1, 0.5, vStrength));
          vec3 finalColor = mix(strengthAdjustedColor, activeColor, vActivation * 0.5); // Reduced from 0.7
          finalColor += vec3(0.1, 0.2, 0.3) * core * 0.7; // Reduced brightness
          
          if (alpha < 0.1) discard;
          gl_FragColor = vec4(finalColor, alpha);
        }
      `,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
    })

    // Create the neuron particle system
    particles = new THREE.Points(geometry, material)
    scene.add(particles)
  }

  function createNeuronGlows() {
    // Create larger glowing auras around neurons for enhanced visual impact
    // Use a sphere geometry with a larger radius
    const geometry = new THREE.SphereGeometry(1, 16, 16)

    const material = new THREE.MeshBasicMaterial({
      color: params.neuronGlowColor,
      opacity: 0.08, // Reduced from 0.15 for subtler glow
      transparent: true,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide, // Changed from BackSide to DoubleSide for more complete glow
      depthWrite: false, // Prevent z-fighting with neurons
    })

    const neuronGlows = new THREE.InstancedMesh(
      geometry,
      material,
      params.particleCount,
    )

    // Set positions and sizes
    const dummy = new THREE.Object3D()
    for (let i = 0; i < params.particleCount; i++) {
      dummy.position.set(
        neuronPositions[i * 3],
        neuronPositions[i * 3 + 1],
        neuronPositions[i * 3 + 2],
      )

      // Larger scale for outer glow
      const scale = params.neuronGlowSize * (0.8 + Math.random() * 0.4)
      dummy.scale.set(scale, scale, scale)

      dummy.updateMatrix()
      neuronGlows.setMatrixAt(i, dummy.matrix)
    }

    neuronGlows.instanceMatrix.needsUpdate = true
    scene.add(neuronGlows)
  }

  function createNeuronSpheres() {
    // Create more interesting cores using phong material with emissive
    const geometry = new THREE.SphereGeometry(params.neuronSize * 0.4, 12, 12)

    // Modified material with adjusted properties for better glow integration
    const material = new THREE.MeshPhongMaterial({
      color: params.neuronColor,
      emissive: new THREE.Color(params.neuronColor).multiplyScalar(0.4),
      specular: new THREE.Color(0xffffff),
      shininess: 100,
      transparent: true,
      opacity: 0.5, // Reduced from 0.7 for better glow integration
      blending: THREE.AdditiveBlending, // Changed to additive blending to blend with glow
      depthWrite: false, // Prevent z-fighting with glow
    })

    // Create instanced mesh for better performance
    neuronSpheres = new THREE.InstancedMesh(
      geometry,
      material,
      params.particleCount,
    )

    // Set initial positions
    const dummy = new THREE.Object3D()
    for (let i = 0; i < params.particleCount; i++) {
      dummy.position.set(
        neuronPositions[i * 3],
        neuronPositions[i * 3 + 1],
        neuronPositions[i * 3 + 2],
      )
      dummy.updateMatrix()
      neuronSpheres.setMatrixAt(i, dummy.matrix)
    }

    neuronSpheres.instanceMatrix.needsUpdate = true
    scene.add(neuronSpheres)
  }

  function createAxons() {
    // Completely revised axon creation system for better connections
    const indices = []
    const axonPositionsArray = []
    const neuronIndicesArray = []

    // Create more purposeful connections rather than completely random
    const connectionMap = new Map()

    // Track connected neurons to ensure no floating neurons
    const connectedNeurons = new Set()

    // Helper to check if connection exists
    const isConnected = (a, b) => {
      const key = a < b ? `${a}-${b}` : `${b}-${a}`
      return connectionMap.has(key)
    }

    // Helper to mark connection
    const markConnected = (a, b) => {
      const key = a < b ? `${a}-${b}` : `${b}-${a}`
      connectionMap.set(key, true)

      // Also mark both neurons as connected
      connectedNeurons.add(a)
      connectedNeurons.add(b)
    }

    // STEP 1: First ensure every neuron has at least one connection
    // This guarantees no disconnected neurons
    for (let i = 0; i < params.particleCount; i++) {
      if (connectedNeurons.has(i)) continue // Already connected

      // Find the closest neuron to connect to
      let closestIdx = -1
      let closestDist = Infinity

      for (let j = 0; j < params.particleCount; j++) {
        if (i === j) continue

        const x1 = neuronPositions[i * 3]
        const y1 = neuronPositions[i * 3 + 1]
        const z1 = neuronPositions[i * 3 + 2]

        const x2 = neuronPositions[j * 3]
        const y2 = neuronPositions[j * 3 + 1]
        const z2 = neuronPositions[j * 3 + 2]

        const dist = Math.sqrt(
          Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2) + Math.pow(z2 - z1, 2),
        )

        if (dist < closestDist) {
          closestDist = dist
          closestIdx = j
        }
      }

      if (closestIdx >= 0) {
        markConnected(i, closestIdx)

        // Get positions
        const x1 = neuronPositions[i * 3]
        const y1 = neuronPositions[i * 3 + 1]
        const z1 = neuronPositions[i * 3 + 2]

        const x2 = neuronPositions[closestIdx * 3]
        const y2 = neuronPositions[closestIdx * 3 + 1]
        const z2 = neuronPositions[closestIdx * 3 + 2]

        // Add to arrays
        axonPositionsArray.push(x1, y1, z1, x2, y2, z2)
        neuronIndicesArray.push(i, closestIdx)

        // Log with reduced frequency
        if (debug.connectionLogs) {
          debugLog(
            "connection",
            `Created mandatory connection from neuron ${i} to ${closestIdx}`,
          )
        }
      }
    }

    // STEP 2: Add proximity-based connections for more natural network
    for (let i = 0; i < params.particleCount; i++) {
      // Find nearby neurons for connections
      const possibleConnections = []

      for (let j = 0; j < params.particleCount; j++) {
        if (i === j || isConnected(i, j)) continue

        const x1 = neuronPositions[i * 3]
        const y1 = neuronPositions[i * 3 + 1]
        const z1 = neuronPositions[i * 3 + 2]

        const x2 = neuronPositions[j * 3]
        const y2 = neuronPositions[j * 3 + 1]
        const z2 = neuronPositions[j * 3 + 2]

        const dist = Math.sqrt(
          Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2) + Math.pow(z2 - z1, 2),
        )

        // Use a much higher connection radius to ensure more connections
        if (dist < params.neuronSpread.x * 1.0) {
          // Increased from 0.8
          possibleConnections.push({ index: j, distance: dist })
        }
      }

      // Sort by distance for better connections
      possibleConnections.sort((a, b) => a.distance - b.distance)

      // Create more connections per neuron - at least 3 connections for each neuron
      // to ensure a well-connected network
      const connectionsToMake = Math.max(
        3, // Every neuron should have at least 3 connections
        Math.min(3 + Math.floor(Math.random() * 3), possibleConnections.length),
      )

      for (let c = 0; c < connectionsToMake; c++) {
        // Skip if we've reached array limits
        if (
          neuronIndicesArray.length >= params.axonCount * 2 ||
          c >= possibleConnections.length
        )
          break

        const target = possibleConnections[c].index
        if (isConnected(i, target)) continue

        markConnected(i, target)

        // Get positions
        const x1 = neuronPositions[i * 3]
        const y1 = neuronPositions[i * 3 + 1]
        const z1 = neuronPositions[i * 3 + 2]

        const x2 = neuronPositions[target * 3]
        const y2 = neuronPositions[target * 3 + 1]
        const z2 = neuronPositions[target * 3 + 2]

        // Add to arrays
        axonPositionsArray.push(x1, y1, z1, x2, y2, z2)
        neuronIndicesArray.push(i, target)
      }
    }

    // Ensure arrays are correct size - explicitly limit to params.axonCount
    const effectiveAxonCount = Math.min(
      params.axonCount,
      Math.floor(neuronIndicesArray.length / 2),
    )

    // Create exactly-sized arrays
    const axonPositionsFinal = new Float32Array(effectiveAxonCount * 6)
    const neuronIndicesFinal = new Int32Array(effectiveAxonCount * 2)

    // Copy data into properly sized arrays
    for (let i = 0; i < effectiveAxonCount; i++) {
      if (
        i * 6 + 5 < axonPositionsArray.length &&
        i * 2 + 1 < neuronIndicesArray.length
      ) {
        // Copy axon positions
        axonPositionsFinal[i * 6] = axonPositionsArray[i * 6]
        axonPositionsFinal[i * 6 + 1] = axonPositionsArray[i * 6 + 1]
        axonPositionsFinal[i * 6 + 2] = axonPositionsArray[i * 6 + 2]
        axonPositionsFinal[i * 6 + 3] = axonPositionsArray[i * 6 + 3]
        axonPositionsFinal[i * 6 + 4] = axonPositionsArray[i * 6 + 4]
        axonPositionsFinal[i * 6 + 5] = axonPositionsArray[i * 6 + 5]

        // Copy neuron indices
        neuronIndicesFinal[i * 2] = neuronIndicesArray[i * 2]
        neuronIndicesFinal[i * 2 + 1] = neuronIndicesArray[i * 2 + 1]
      }
    }

    // Use properly sized arrays
    axonPositions = axonPositionsFinal
    neuronIndices = neuronIndicesFinal

    // Set up indices with the correct count
    for (let i = 0; i < effectiveAxonCount; i++) {
      indices.push(i * 2, i * 2 + 1)
    }

    // Update global parameters to match actual count
    params.axonCount = effectiveAxonCount

    // Only log the total connections, not individual ones
    debugLog(
      "network",
      `Created ${effectiveAxonCount} axons connecting neurons`,
      true,
    )

    // Create geometry for axons with better visual quality
    const axonGeometry = new THREE.BufferGeometry()
    axonGeometry.setAttribute(
      "position",
      new THREE.BufferAttribute(axonPositions, 3),
    )
    axonGeometry.setIndex(indices)

    // Create visible line segments with custom activation shaders - FIX color value
    const axonMaterial = new THREE.ShaderMaterial({
      uniforms: {
        color: { value: params.axonColor }, // Use params.axonColor directly instead of || fallback
        pulseColor: { value: params.pulseColor },
        time: { value: 0.0 },
        pulsePositions: {
          value: new Float32Array(params.axonCount * 4).fill(-1),
        }, // [axonIndex, progress, intensity, direction]
        pulseWidth: { value: params.axonPulseWidth * 1.0 }, // Wider pulses (was 0.8)
        pulseIntensity: { value: params.axonPulseIntensity * 1.2 }, // Higher pulse intensity (was 0.9)
      },
      vertexShader: `
        // THREE.js already defines position, modelViewMatrix, and projectionMatrix
        // No need to redefine them
        
        // For passing to fragment shader
        varying vec3 vPosition;
        varying float vAxonIndex;
        
        void main() {
          // Calculate which axon this vertex belongs to based on position in buffer
          // Each line has 2 vertices
          vAxonIndex = float(floor(float(gl_VertexID) / 2.0));
          vPosition = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 color;
        uniform vec3 pulseColor;
        uniform float time;
        uniform float pulsePositions[${params.axonCount * 4}];
        uniform float pulseWidth;
        uniform float pulseIntensity;
        
        varying vec3 vPosition;
        varying float vAxonIndex;
        
        void main() {
          // Default fragment color is axon color
          vec3 fragColor = color;
          float alpha = 0.7; // Increased from 0.5 for better visibility
          
          // For each axon, get its pulse position
          int axonIdx = int(vAxonIndex);
          int pulseIdx = axonIdx * 4;
          
          // Get pulse information
          float pulseActive = pulsePositions[pulseIdx];
          float pulseProgress = pulsePositions[pulseIdx + 1];
          float pulseIntensityValue = pulsePositions[pulseIdx + 2];
          float pulseDirection = pulsePositions[pulseIdx + 3];
          
          if (pulseActive >= 0.0) {
            // This creates a smooth gradient along the axon for better glow distribution
            float positionOnLine = length(vPosition) * 0.005; // Scaled position along axon
            
            // Calculate distance from pulse with smoother falloff
            float distFromPulse = abs(positionOnLine - pulseProgress);
            
            // Wider pulse width
            float pulseWidthFactor = pulseWidth * 0.03 * (1.0 + pulseIntensityValue * 1.5); // Increased from 0.02/1.2
            
            // Apply pulse if within the pulse width with smoother gradient
            if (distFromPulse < pulseWidthFactor) {
              // Smoother falloff using smoothstep for a more organic look
              float pulseEffect = 1.0 - smoothstep(0.0, pulseWidthFactor, distFromPulse);
              pulseEffect = pow(pulseEffect, 1.1) * pulseIntensityValue * pulseIntensity; // Less steep power curve (was 1.2)
              
              // More dramatic color blending
              fragColor = mix(color, pulseColor, min(0.9, pulseEffect * 0.8)); // Increased from 0.8/0.6
              
              // More substantial brightness boost
              fragColor *= 1.0 + pulseEffect * 2.5; // Increased from 1.8
              
              // Higher alpha for brighter pulses
              alpha = min(0.95, 0.7 + pulseEffect * 0.5); // Increased from 0.5+effect*0.4
            }
          }
          
          gl_FragColor = vec4(fragColor, alpha);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })

    // Create line segments for visible axons
    const visibleAxons = new THREE.LineSegments(axonGeometry, axonMaterial)
    scene.add(visibleAxons)
    window.visibleAxons = visibleAxons // Keep reference for updates

    // Keep invisible reference for axon positions
    const axonLines = new THREE.LineSegments(
      axonGeometry.clone(),
      new THREE.LineBasicMaterial({
        visible: false,
      }),
    )
    scene.add(axonLines)
    window.axonLinesReference = axonLines

    // Initialize pulse position array (empty/inactive initially)
    window.axonPulseInfo = new Float32Array(effectiveAxonCount * 4).fill(-1)

    // Initialize connection strengths with lower values for a more subdued start
    for (let i = 0; i < params.axonCount; i++) {
      axonStrengths[i] = 0.25 + Math.random() * 0.25 // Reduced from 0.4-1.0 to 0.25-0.5
      lastUsedTime[i] = -Math.random() * 50 // Longer staggered times
    }

    // Use an instanced mesh for axons to enable individual strength display
    const axonInstanceGeometry = new THREE.CylinderGeometry(
      params.axonRadius, // radius top
      params.axonRadius, // radius bottom
      1, // height (will be scaled)
      6, // radial segments
      1, // height segments
      true, // open-ended
    )

    // Use a different variable name for the instance material to avoid redeclaration
    const axonInstanceMaterial = new THREE.MeshBasicMaterial({
      color: params.axonColor, // Use the correct color from params
      transparent: true,
      opacity: 0.5,
      blending: THREE.AdditiveBlending,
    })

    // Create instanced mesh for axons
    const axonInstances = new THREE.InstancedMesh(
      axonInstanceGeometry,
      axonInstanceMaterial,
      params.axonCount,
    )

    // Store for updates - but keep both representations
    axons = axonInstances
    scene.add(axonInstances)

    // Initial positioning of all axon instances
    updateAxonInstancePositions()
  }

  // New function to update axon appearances based on strength
  function updateAxonInstancePositions() {
    const dummy = new THREE.Object3D()
    const quaternion = new THREE.Quaternion()
    const up = new THREE.Vector3(0, 1, 0)

    for (let i = 0; i < params.axonCount; i++) {
      if (
        deadAxons.has(i) ||
        i * 6 + 5 >= axonPositions.length ||
        i * 2 + 1 >= neuronIndices.length
      ) {
        // Skip dead axons
        axons.setMatrixAt(i, new THREE.Matrix4().makeScale(0, 0, 0))
        continue
      }

      // Get neuron indices for this axon
      const sourceIdx = neuronIndices[i * 2]
      const targetIdx = neuronIndices[i * 2 + 1]

      if (
        sourceIdx < 0 ||
        targetIdx < 0 ||
        sourceIdx >= params.particleCount ||
        targetIdx >= params.particleCount
      ) {
        continue
      }

      // Get neuron positions - use particle positions for more accurate connections
      const neuronPos = particles.geometry.attributes.position.array

      // Check if positions are within array bounds
      if (
        sourceIdx * 3 + 2 < neuronPos.length &&
        targetIdx * 3 + 2 < neuronPos.length
      ) {
        // Get start and end positions
        const start = new THREE.Vector3(
          neuronPos[sourceIdx * 3],
          neuronPos[sourceIdx * 3 + 1],
          neuronPos[sourceIdx * 3 + 2],
        )

        const end = new THREE.Vector3(
          neuronPos[targetIdx * 3],
          neuronPos[targetIdx * 3 + 1],
          neuronPos[targetIdx * 3 + 2],
        )

        // Update axon positions array for future reference
        axonPositions[i * 6] = start.x
        axonPositions[i * 6 + 1] = start.y
        axonPositions[i * 6 + 2] = start.z
        axonPositions[i * 6 + 3] = end.x
        axonPositions[i * 6 + 4] = end.y
        axonPositions[i * 6 + 5] = end.z

        // Calculate axon parameters
        const direction = new THREE.Vector3().subVectors(end, start)
        const length = direction.length()
        direction.normalize()

        // Compute rotation to align cylinder with direction
        quaternion.setFromUnitVectors(up, direction)

        // Position in the middle
        const midpoint = new THREE.Vector3().addVectors(
          start,
          end.clone().sub(start).multiplyScalar(0.5),
        )

        // Set transform matrix for this axon instance
        dummy.position.copy(midpoint)
        dummy.quaternion.copy(quaternion)

        // Scale based on strength and length
        const strength = axonStrengths[i]
        // Apply exponential scaling for better visibility of strong connections
        const scaleFactor = 0.3 + Math.pow(strength, 1.5) * 0.7

        dummy.scale.set(
          params.axonRadius * scaleFactor,
          length * 0.5, // Half length because the cylinder is centered
          params.axonRadius * scaleFactor,
        )

        dummy.updateMatrix()
        axons.setMatrixAt(i, dummy.matrix)

        // Set color based on strength with higher contrast
        const color = new THREE.Color(params.axonColor)
        if (strength < params.axonMinStrength) {
          // Fade out weak axons
          const fadeRatio = Math.max(0, strength / params.axonMinStrength)
          axons.setColorAt(i, color.multiplyScalar(fadeRatio * 0.8))
        } else {
          // Strong axons are brighter with more dramatic scaling
          const brightnessFactor = 0.8 + Math.pow(strength, 1.2) * 0.5
          axons.setColorAt(i, color.multiplyScalar(brightnessFactor))
        }
      }
    }

    // Update the instance matrices
    axons.instanceMatrix.needsUpdate = true

    // Update instance colors if available
    if (axons.instanceColor) {
      axons.instanceColor.needsUpdate = true
    }
  }

  function createActivations() {
    // Create activation pulses that travel along axons
    const geometry = new THREE.BufferGeometry()
    const positions = new Float32Array(params.activationCount * 3)
    const progresses = new Float32Array(params.activationCount)
    const axonIndices = new Float32Array(params.activationCount)
    const sizes = new Float32Array(params.activationCount)
    const directions = new Float32Array(params.activationCount)
    const intensities = new Float32Array(params.activationCount)

    // Initialize activation pulses with more moderate sizes
    for (let i = 0; i < params.activationCount; i++) {
      axonIndices[i] = -1 // Inactive initially
      progresses[i] = Math.random()
      sizes[i] = Math.random() * 1.8 + 1.5 // More moderate sizes
      directions[i] = Math.random() > 0.5 ? 1 : 0
      intensities[i] = 0.0

      // Set initial positions to origin (will be updated in shader)
      positions[i * 3] = 0
      positions[i * 3 + 1] = 0
      positions[i * 3 + 2] = 0
    }

    // Set attributes
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute("progress", new THREE.BufferAttribute(progresses, 1))
    geometry.setAttribute(
      "axonIndex",
      new THREE.BufferAttribute(axonIndices, 1),
    )
    geometry.setAttribute("size", new THREE.BufferAttribute(sizes, 1))
    geometry.setAttribute("direction", new THREE.BufferAttribute(directions, 1))
    geometry.setAttribute(
      "intensity",
      new THREE.BufferAttribute(intensities, 1),
    )

    // Create uniforms for activation shader with toned down values
    activationUniforms = {
      time: { value: 0.0 },
      color: { value: params.activationColor },
      pointSize: { value: params.activationSize * 1.2 }, // Reduced from 2.5x
      axonPositions: { value: axonPositions },
      pulseSpeed: { value: 5.0 }, // Slower pulsing
      pulseStrength: { value: 0.6 }, // Reduced pulse strength
    }

    // Enhanced shader for activations with more subtle glow effect
    const material = new THREE.ShaderMaterial({
      uniforms: activationUniforms,
      vertexShader: `
        uniform float time;
        uniform float pointSize;
        uniform float axonPositions[${params.axonCount * 6}];
        uniform float pulseSpeed;
        uniform float pulseStrength;
        
        attribute float progress;
        attribute float axonIndex;
        attribute float size;
        attribute float direction;
        attribute float intensity;
        
        varying float vIntensity;
        varying float vProgress;
        varying float vSize;
        varying vec3 vDirection; // For elongating the glow in movement direction
        
        void main() {
          vProgress = progress;
          vSize = size;
          vIntensity = intensity;
          
          // Skip inactive activations
          if (axonIndex < 0.0) {
            gl_Position = vec4(0.0, 0.0, -1000.0, 1.0); // Move far away
            gl_PointSize = 0.0;
            return;
          }
          
          // Get axon start and end positions
          int idx = int(axonIndex) * 6;
          vec3 start = vec3(
            axonPositions[idx],
            axonPositions[idx+1],
            axonPositions[idx+2]
          );
          vec3 end = vec3(
            axonPositions[idx+3],
            axonPositions[idx+4],
            axonPositions[idx+5]
          );
          
          // Calculate movement direction for elongating the glow
          vec3 axonDir = normalize(end - start);
          vDirection = axonDir; // Pass to fragment shader
          
          // Calculate current position along axon with a gentler curve
          float t = progress; // Current position (0-1)
          vec3 midPoint = (start + end) * 0.5;
          // Add moderate height to the midpoint for a curved path
          midPoint.y += length(end - start) * 0.08;
          
          // Quadratic bezier curve for smoother movement
          vec3 pos = mix(mix(start, midPoint, t), mix(midPoint, end, t), t);
          
          // Add subtle displacement for organic motion
          float displacement = sin(time * 3.0 + axonIndex * 10.0) * 0.4;
          pos.x += displacement * intensity * 0.3;
          pos.y += displacement * intensity * 0.3;
          
          // More gentle pulsating size effect
          float primaryPulse = sin(time * pulseSpeed * 1.5 + axonIndex);
          float secondaryPulse = sin(time * pulseSpeed * 0.5 + axonIndex * 3.1415);
          float pulseEffect = 1.0 + pulseStrength * (primaryPulse * 0.6 + secondaryPulse * 0.2);
          
          // More moderate size boost with intensity
          float sizeMultiplier = 1.5 + intensity * 2.0;
          
          gl_PointSize = pointSize * size * pulseEffect * sizeMultiplier;
          
          // Apply position
          vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform vec3 color;
        uniform float time;
        
        varying float vIntensity;
        varying float vProgress;
        varying float vSize;
        varying vec3 vDirection;
        
        void main() {
          // Create gently elongated glowing bulge
          vec2 xy = gl_PointCoord.xy - vec2(0.5);
          
          // More subtle elongation
          float elongation = 1.2 + vIntensity * 0.8;
          vec2 stretched;
          if (abs(vDirection.x) > abs(vDirection.y)) {
            // Horizontally dominant direction
            stretched = vec2(xy.x / elongation, xy.y);
          } else {
            // Vertically dominant direction
            stretched = vec2(xy.x, xy.y / elongation);
          }
          
          float radius = length(stretched);
          
          // Create multi-layer glow with softer transitions
          float core = 1.0 - smoothstep(0.0, 0.25, radius);
          float innerGlow = 1.0 - smoothstep(0.25, 0.4, radius);
          float outerGlow = 1.0 - smoothstep(0.4, 0.5, radius);
          
          // Combine layers for a gentler, more voluminous appearance
          float glowStrength = core * 0.7 + innerGlow * 0.5 + outerGlow * 0.2;
          
          // More subtle glow modulation
          float timePulse = 0.1 * sin(time * 6.0 + vProgress * 15.0);
          glowStrength *= 0.65 + vIntensity * 0.5 + timePulse;
          
          // More transparent for subtlety
          float alpha = glowStrength * min(0.8, 0.5 + vIntensity * 0.5);
          
          // Gentler color blending
          vec3 coreColor = mix(color, vec3(1.0, 1.0, 1.0), vIntensity * 0.6);
          vec3 edgeColor = mix(color, vec3(0.3, 0.6, 1.0), vIntensity * 0.3);
          
          // Blend colors for final effect
          vec3 finalColor = mix(edgeColor, coreColor, smoothstep(0.0, 0.3, 1.0-radius));
          
          // Moderate vibrance boost
          finalColor *= 1.0 + vIntensity * 0.3;
          
          if (alpha < 0.05) discard;
          gl_FragColor = vec4(finalColor, alpha);
        }
      `,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
    })

    // Create the activation pulse system
    activations = new THREE.Points(geometry, material)
    scene.add(activations)
  }

  // Remove the function that creates orbiting particles around neurons
  function createParticleEffects() {
    // Return an empty object instead of creating particles
    return {
      points: null,
      uniforms: {
        time: { value: 0 },
        neuronPositions: { value: neuronPositions },
      },
    }
  }

  // Keep the second occurrence with its original name
  function updateNeuronSpheres() {
    // Update neuron spheres to follow neurons
    const neuronPos = particles.geometry.attributes.position.array
    const dummy = new THREE.Object3D()

    for (let i = 0; i < params.particleCount; i++) {
      dummy.position.set(
        neuronPos[i * 3],
        neuronPos[i * 3 + 1],
        neuronPos[i * 3 + 2],
      )
      dummy.updateMatrix()
      neuronSpheres.setMatrixAt(i, dummy.matrix)
    }

    neuronSpheres.instanceMatrix.needsUpdate = true
  }

  function updateAxons() {
    // Get current neuron positions
    const neuronPos = particles.geometry.attributes.position.array

    // Make sure we don't exceed array bounds - fix disconnection issue
    const axonCount = Math.min(
      Math.floor(neuronIndices.length / 2),
      params.axonCount,
    )
    const axonPosLength = axonPositions.length / 6

    // Use actual computed neuron positions to update axons positions buffer
    for (let i = 0; i < axonCount; i++) {
      // Skip invalid or dead axons
      if (deadAxons.has(i)) continue

      // Safe bounds checks
      if (i >= axonPosLength) continue

      const neuronIdx1 = neuronIndices[i * 2]
      const neuronIdx2 = neuronIndices[i * 2 + 1]

      // Skip invalid indices
      if (
        neuronIdx1 < 0 ||
        neuronIdx2 < 0 ||
        neuronIdx1 >= params.particleCount ||
        neuronIdx2 >= params.particleCount
      ) {
        continue
      }

      // Ensure we have valid positions for both neuron endpoints
      if (
        neuronIdx1 * 3 + 2 < neuronPos.length &&
        neuronIdx2 * 3 + 2 < neuronPos.length
      ) {
        // Update the axon position buffer to match neuron positions
        axonPositions[i * 6] = neuronPos[neuronIdx1 * 3]
        axonPositions[i * 6 + 1] = neuronPos[neuronIdx1 * 3 + 1]
        axonPositions[i * 6 + 2] = neuronPos[neuronIdx1 * 3 + 2]

        axonPositions[i * 6 + 3] = neuronPos[neuronIdx2 * 3]
        axonPositions[i * 6 + 4] = neuronPos[neuronIdx2 * 3 + 1]
        axonPositions[i * 6 + 5] = neuronPos[neuronIdx2 * 3 + 2]
      }
    }

    // Update the underlying buffer used for activation positions
    if (window.axonLinesReference?.geometry) {
      const axonLinesGeometry = window.axonLinesReference.geometry
      axonLinesGeometry.setAttribute(
        "position",
        new THREE.BufferAttribute(axonPositions, 3),
      )
      axonLinesGeometry.attributes.position.needsUpdate = true
    }

    // Also update the uniform in the activation shader
    activationUniforms.axonPositions.value = axonPositions

    // Also update the shader uniforms for pulses
    if (window.visibleAxons?.material?.uniforms) {
      window.visibleAxons.material.uniforms.time.value = clock.getElapsedTime()
      window.visibleAxons.material.uniforms.pulsePositions.value =
        window.axonPulseInfo
    }

    // No need to update the axons geometry directly since we're using instances
  }

  function animate() {
    animationId = requestAnimationFrame(animate)

    const time = clock.getElapsedTime()
    const now = performance.now()
    const elapsedTime = time - startTime // Time since initialization
    const deltaTime = clock.getDelta()

    // Smooth mouse position interpolation for more fluid interaction
    if (mouseActivationEnabled) {
      // Interpolate current mouse position towards target for smoother movement
      mousePosition.lerp(targetMousePosition, 0.2)

      // Process mouse intersection with the smoothed position
      processMouseIntersection(false)

      // Update hover intensity for smoother hover effect
      if (currentHoveredNeuron !== null) {
        // Calculate how long we've been hovering (capped at hoverTransitionDuration)
        const hoverTime = Math.min(
          now - hoverStartTime,
          hoverTransitionDuration,
        )
        const hoverProgress = hoverTime / hoverTransitionDuration

        // Smoothly increase intensity up to full mouseHoverIntensity
        const currentIntensity =
          1.2 + (mouseHoverIntensity - 1.2) * hoverProgress

        // If the neuron is still active, boost its intensity
        const activeData = activeNeurons.get(currentHoveredNeuron)
        if (activeData) {
          // Smoothly boost intensity
          const newIntensity = Math.max(
            activeData.intensity,
            currentIntensity * 0.8,
          )
          activeData.intensity = newIntensity

          // Apply to shader for visual feedback
          if (uniforms.activeNeurons?.value) {
            uniforms.activeNeurons.value[currentHoveredNeuron] = newIntensity
          }
        }
      }
    }

    // Control startup animation phases
    if (startupPhase === 0 && elapsedTime > 1.0) {
      // After 1 second, network structure becomes visible
      startupPhase = 1
      debugLog("phase", "Phase 1: Network structure visible", true)
    } else if (startupPhase === 1 && elapsedTime > fadeInDuration) {
      // After fadeInDuration seconds, start mild activations
      startupPhase = 2
      debugLog("phase", "Phase 2: Beginning mild activations", true)
      initializeFirstActivations()
      startupIntensityMultiplier = 0.5 // Start activations at 50% intensity
    } else if (startupPhase === 2 && elapsedTime > fullStrengthTime) {
      // After fullStrengthTime seconds, start full activation cycle
      startupPhase = 3
      debugLog("phase", "Phase 3: Full activation cycle", true)
      startActivationCycle()
      startupIntensityMultiplier = 1.0 // Full intensity for regular cycle
    }

    // Calculate fade-in factor (0-1) for each visual element
    const structureFade = Math.min(1.0, elapsedTime / 2.0) // Structure fades in over 2 seconds
    const neuronFade = Math.min(1.0, Math.max(0, (elapsedTime - 0.5) / 2.0)) // Neurons fade in over 2 seconds, starting at 0.5s
    const activationFade =
      startupPhase >= 2
        ? Math.min(1.0, (elapsedTime - fadeInDuration) / 3.0)
        : 0 // Activations fade in over 3 seconds

    // Apply fade factors to visual elements
    if (axons && axons.material) {
      if (axons.material instanceof THREE.MeshBasicMaterial) {
        axons.material.opacity = structureFade * 0.5
      }
    }

    // FIX: Add proper null-checking for color.value before using multiplyScalar
    if (window.visibleAxons?.material?.uniforms?.color?.value) {
      // Create a temporary color to apply the fade to avoid modifying the original
      const tempColor =
        window.visibleAxons.material.uniforms.color.value.clone()
      tempColor.multiplyScalar(structureFade)
      window.visibleAxons.material.uniforms.color.value = tempColor
    } else if (window.visibleAxons?.material?.uniforms?.color) {
      // If color exists but value doesn't, initialize it with a default color
      const defaultColor = new THREE.Color(params.axonColor || 0x3e68ff)
      defaultColor.multiplyScalar(structureFade)
      window.visibleAxons.material.uniforms.color.value = defaultColor
    }

    // Update all uniforms
    uniforms.time.value = time
    activationUniforms.time.value = time * 0.2

    // Update activations network
    updateNeuronActivations(time)
    updateActivations(time, deltaTime)
    updateNeuronShaderUniforms()
    updateAxonStrengths(time, deltaTime) // Add strength management

    // Skip updating particle effects since we don't want orbiting blobs
    // If we have particle effects, update them
    // if (window.particleEffects?.uniforms) {
    //   window.particleEffects.uniforms.time.value = time * 0.5
    //   window.particleEffects.uniforms.neuronPositions.value =
    //     particles.geometry.attributes.position.array
    // }

    // Update positions
    updateAxons()
    updateNeuronSpheres()
    updateAxonInstancePositions() // Make sure instance positions reflect strength

    // Enhanced camera movement for more subtle, elegant viewing angle
    const cameraTime = time * 0.03 // Slower movement (was 0.05)

    // Apply subtle camera adjustment based on mouse position when interaction enabled
    let mouseInfluenceX = 0
    let mouseInfluenceY = 0

    if (mouseActivationEnabled) {
      // Apply subtle camera movement based on mouse position
      mouseInfluenceX = mousePosition.x * 8 // Gentle movement adjustment
      mouseInfluenceY = mousePosition.y * 5 // Subtle vertical adjustment
    }

    camera.position.x =
      Math.sin(cameraTime) * 20 +
      Math.sin(cameraTime * 0.4) * 8 +
      mouseInfluenceX
    camera.position.y = Math.cos(cameraTime * 0.5) * 12 + 15 + mouseInfluenceY
    camera.position.z = 130 + Math.sin(cameraTime * 0.15) * 15

    // Look at a point shifted slightly by mouse position
    camera.lookAt(
      Math.sin(cameraTime * 0.2) * 7 + mouseInfluenceX * 0.3,
      Math.cos(cameraTime * 0.15) * 4 + mouseInfluenceY * 0.2,
      0,
    )

    // Render with post-processing if available
    if (composer && params.usePostProcessing) {
      composer.render()
    } else {
      renderer.render(scene, camera)
    }
  }

  // Add the missing function for initial activations
  function initializeFirstActivations() {
    debugLog("startup", "Initializing mild first activations...", true)

    // Create a few strategic activations to start
    const centerNeurons = []

    // Find neurons near the center for initial activations
    for (let i = 0; i < params.particleCount; i++) {
      const distFromCenter = Math.sqrt(
        Math.pow(neuronPositions[i * 3], 2) +
          Math.pow(neuronPositions[i * 3 + 1], 2) +
          Math.pow(neuronPositions[i * 3 + 2], 2),
      )

      if (distFromCenter < 40) {
        // Center neurons
        centerNeurons.push(i)
      }
    }

    // Choose a few central neurons to activate with increasing intensity
    const neuronCount = Math.min(5, centerNeurons.length)
    for (let i = 0; i < neuronCount; i++) {
      const randomIndex = Math.floor(Math.random() * centerNeurons.length)
      const neuronIdx = centerNeurons[randomIndex]

      // Remove this neuron from the array
      centerNeurons.splice(randomIndex, 1)

      // Activate with increasing intensity for cascading effect
      const delay = i * 300 // Stagger activations
      setTimeout(() => {
        // Scale intensity by startup multiplier
        const scaledIntensity = (1.6 + i * 0.2) * startupIntensityMultiplier
        triggerNeuronActivation(neuronIdx, scaledIntensity)
      }, delay)
    }

    // Schedule a second wave after a short delay
    setTimeout(() => {
      for (let i = 0; i < 3; i++) {
        const neuronIdx = Math.floor(Math.random() * params.particleCount)
        // Scale intensity by startup multiplier
        triggerNeuronActivation(neuronIdx, 1.8 * startupIntensityMultiplier)
      }
    }, 2000)
  }

  // Fix the updateNeuronActivations function (remove duplicate code)
  function updateNeuronActivations(time) {
    // Process active neurons (decay effect over time)
    activeNeurons.forEach((data, neuronIndex) => {
      // Decay based on parameter
      data.intensity *= params.activationDecayRate

      // Update shader activation values so they show up visually
      if (uniforms.activeNeurons?.value && neuronIndex < params.particleCount) {
        uniforms.activeNeurons.value[neuronIndex] = data.intensity * 1.2 // Boost visual intensity
      }

      // Remove when too weak
      if (data.intensity < 0.05) {
        activeNeurons.delete(neuronIndex)
        // Clear shader activation
        if (
          uniforms.activeNeurons?.value &&
          neuronIndex < params.particleCount
        ) {
          uniforms.activeNeurons.value[neuronIndex] = 0
        }
      }
      // Try to branch to connected neurons when activation is fresh
      else if (
        data.timeSinceActivation !== undefined &&
        time - data.timeSinceActivation > params.branchingDelay &&
        !data.hasBranched &&
        data.intensity > 0.3 // Only branch if still reasonably bright
      ) {
        data.hasBranched = true

        // Get available connections
        // Look through all axons to find connections to this neuron
        // Ensure we never exceed actual axon count
        const maxAxonIndex = Math.min(
          Math.floor(neuronIndices.length / 2),
          params.axonCount,
        )
        const availableConnections = []
        for (let i = 0; i < maxAxonIndex; i++) {
          // Skip dead axons
          if (deadAxons.has(i)) continue

          // Bounds check for neuronIndices
          if (i * 2 + 1 >= neuronIndices.length) continue

          const sourceIdx = neuronIndices[i * 2]
          const targetIdx = neuronIndices[i * 2 + 1]

          // Validate neuron indices
          if (
            sourceIdx < 0 ||
            targetIdx < 0 ||
            sourceIdx >= params.particleCount ||
            targetIdx >= params.particleCount
          ) {
            continue
          }

          // If this neuron is connected to the axon
          if (sourceIdx === neuronIndex || targetIdx === neuronIndex) {
            // Get the connection strength with a minimum threshold to ensure connections
            const strength = Math.max(0.3, axonStrengths[i] || 0.5)

            // Consider all but the weakest connections
            if (strength > params.axonMinStrength * 0.8) {
              // Lowered threshold
              availableConnections.push({
                axonIndex: i,
                isOutgoing: sourceIdx === neuronIndex,
                strength: strength,
                targetNeuron: sourceIdx === neuronIndex ? targetIdx : sourceIdx,
              })
            }
          }
        }

        // Sort connections by strength if we want to prefer stronger ones
        if (params.preferStrongerConnections > 0) {
          availableConnections.sort((a, b) => b.strength - a.strength)
        }

        // Use more connections for dramatic branching
        const connectionsToUse = Math.min(
          Math.ceil(availableConnections.length * 0.7), // Use 70% of available connections
          params.activationMaxBranches,
        )

        // Higher success rate for branching to create better waves
        let branchCount = 0
        for (let i = 0; i < connectionsToUse; i++) {
          const conn = availableConnections[i]
          const positionBonus = Math.max(0, 1 - i / connectionsToUse)

          // Much higher propagation chance - almost guaranteed for first few
          // connections to create clear wave fronts
          if (
            Math.random() <
            (i < 3 ? 0.99 : params.activationPropagationChance) *
              Math.sqrt(conn.strength) *
              (1 + positionBonus)
          ) {
            // Pass through more intensity to connected neurons
            const passedIntensity = data.intensity * (0.9 - i * 0.05)

            triggerNeuronActivation(conn.targetNeuron, passedIntensity)

            // Create brighter and more visible pulse
            if (conn.isOutgoing) {
              createPulseOnAxon(conn.axonIndex, 0, 1, passedIntensity * 1.3)
            } else {
              createPulseOnAxon(conn.axonIndex, 1, 0, passedIntensity * 1.3)
            }

            branchCount++
          }
        }

        // If we found connections but none are above threshold, take the best one anyway
        if (availableConnections.length > 0 && branchCount === 0) {
          const bestConnection = availableConnections[0]
          const conn = bestConnection

          // Activate with higher intensity for visibility
          triggerNeuronActivation(
            conn.targetNeuron,
            Math.min(1.5, data.intensity * 1.1),
          )

          // Create pulse with higher brightness
          if (conn.isOutgoing) {
            createPulseOnAxon(
              conn.axonIndex,
              0,
              1,
              Math.min(1.9, data.intensity * 1.4),
            )
          } else {
            createPulseOnAxon(
              conn.axonIndex,
              1,
              0,
              Math.min(1.9, data.intensity * 1.4),
            )
          }
          branchCount++
        }

        // Log successful branching
        if (branchCount > 0) {
          debugLog(
            "activation",
            `Neuron ${neuronIndex} branched to ${branchCount} connections with intensity ${data.intensity}`,
          )
        }
      }
    })

    // GREATLY reduce random activations to focus on wave patterns
    if (activeNeurons.size < 2 && Math.random() < 0.002) {
      // When creating random activations, make them dramatic
      const randomNeuron = Math.floor(Math.random() * params.particleCount)
      triggerNeuronActivation(randomNeuron, 2.2) // Higher intensity for better propagation
    }

    // Force pulse creation less frequently for more distinct activation patterns
    if (Math.random() < 0.03) {
      // Reduced from 0.08
      // Less frequent pulses
      const randomNeuron = Math.floor(Math.random() * params.particleCount)
      triggerNeuronActivation(randomNeuron, 1.6) // Lower intensity

      // Create pulses
      startPulsesFromNeuron(randomNeuron)
    }
  }

  // Improved neuron activation function that handles intensity
  function triggerNeuronActivation(neuronIndex, intensity = 1.0) {
    if (
      !neuronIndex ||
      neuronIndex < 0 ||
      neuronIndex >= params.particleCount
    ) {
      return // Skip invalid neurons
    }

    // Scale intensity by the current startup multiplier
    intensity *= startupIntensityMultiplier

    // Get current intensity or use default
    let currentIntensity = 0
    const existingData = activeNeurons.get(neuronIndex)
    if (existingData) {
      currentIntensity = existingData.intensity
    }

    // Use the higher intensity between current and new
    const finalIntensity = Math.max(currentIntensity, intensity)
    const wasInactive = !existingData || existingData.intensity < 0.3

    // Set or update the neuron data - IMPORTANT: reset hasBranched flag when
    // receiving a new strong activation to create branching cascades
    const shouldResetBranching =
      finalIntensity > (existingData?.intensity || 0) * 1.5

    activeNeurons.set(neuronIndex, {
      intensity: finalIntensity,
      timeSinceActivation: clock.getElapsedTime(),
      hasBranched: existingData?.hasBranched && !shouldResetBranching,
    })

    // Immediately update shader uniforms for instant visual feedback
    if (uniforms.activeNeurons?.value && neuronIndex < params.particleCount) {
      uniforms.activeNeurons.value[neuronIndex] = finalIntensity * 1.6 // Boosted for better visibility
    }

    // If this was a newly activated neuron or a significant intensity increase,
    // immediately start some pulses to show activation spreading
    if (wasInactive || shouldResetBranching) {
      setTimeout(() => {
        startPulsesFromNeuron(neuronIndex, finalIntensity * 1.2)
      }, 10) // Small delay to make it more visible after the neuron lights up
    }

    // Log activations with throttling
    if (debug.activationLogs && Math.random() < debug.logFrequency) {
      debugLog(
        "activation",
        `Neuron ${neuronIndex} activated with intensity ${finalIntensity.toFixed(2)}`,
      )
    }
  }

  // Create more pulses from an active neuron with improved bounds checking
  function startPulsesFromNeuron(neuronIndex, baseIntensity = 1.4) {
    let createdPulses = 0
    let axonIndicesForThisNeuron = []

    // Calculate exact length of valid neuronIndices array
    const neuronIndicesLength = neuronIndices.length
    // Make sure we don't exceed the maximum possible axon count
    const effectiveAxonCount = Math.min(
      params.axonCount,
      Math.floor(neuronIndicesLength / 2),
    )

    // First, collect all axons connected to this neuron with stricter validation
    for (let i = 0; i < effectiveAxonCount; i++) {
      // Skip if out of bounds in neuron indices array
      if (i * 2 + 1 >= neuronIndicesLength) continue

      const sourceIdx = neuronIndices[i * 2]
      const targetIdx = neuronIndices[i * 2 + 1]

      // Skip invalid neuron indices
      if (
        sourceIdx < 0 ||
        targetIdx < 0 ||
        sourceIdx >= params.particleCount ||
        targetIdx >= params.particleCount
      ) {
        continue
      }

      if (sourceIdx === neuronIndex || targetIdx === neuronIndex) {
        axonIndicesForThisNeuron.push({
          axonIndex: i,
          isOutgoing: neuronIndices[i * 2] === neuronIndex,
        })
      }
    }

    // If no connections, do nothing
    if (axonIndicesForThisNeuron.length === 0) {
      return
    }

    // Create more pulses for more dramatic branching (3-6 vs 3-5)
    const pulsesToCreate = Math.min(
      3 + Math.floor(Math.random() * 4),
      axonIndicesForThisNeuron.length,
    )

    // Shuffle the array for random selection
    for (let i = axonIndicesForThisNeuron.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[axonIndicesForThisNeuron[i], axonIndicesForThisNeuron[j]] = [
        axonIndicesForThisNeuron[j],
        axonIndicesForThisNeuron[i],
      ]
    }

    // Create pulses on selected axons with higher intensity
    for (let i = 0; i < pulsesToCreate; i++) {
      const connection = axonIndicesForThisNeuron[i]

      // Higher intensity for first pulses to create dramatic branching
      const pulseIntensity =
        baseIntensity * (i === 0 ? 1.4 : 1.0 - (i / pulsesToCreate) * 0.3)

      createPulseOnAxon(
        connection.axonIndex,
        connection.isOutgoing ? 0 : 1, // Start at this neuron's end
        connection.isOutgoing ? 1 : 0, // Direction away from this neuron
        pulseIntensity, // Higher intensity with falloff for subsequent branches
      )
      createdPulses++
    }

    // Log pulses with throttling
    if (debug.pulseLogs && createdPulses > 0) {
      debugLog(
        "pulse",
        `Created ${createdPulses} pulses from neuron ${neuronIndex} with intensity ${baseIntensity.toFixed(2)}`,
      )
    }

    return createdPulses
  }

  // Corrected version of createPulseOnAxon - properly closed and structured
  function createPulseOnAxon(axonIndex, startProgress, direction, intensity) {
    // Enhanced validation for axon index
    if (
      axonIndex === undefined ||
      axonIndex === null ||
      typeof axonIndex !== "number"
    ) {
      console.error(`Invalid axon index type: ${typeof axonIndex}`)
      return
    }

    // Strict range check
    if (axonIndex < 0 || axonIndex >= params.axonCount) {
      console.error(
        `Invalid axon index: ${axonIndex} (valid range: 0-${params.axonCount - 1})`,
      )
      return
    }

    // Skip dead axons
    if (deadAxons.has(axonIndex)) {
      return
    }

    const activationAttr = activations.geometry.attributes

    // Find available slot OR reuse a low-intensity slot
    let slotIndex = -1
    let lowestIntensity = 0.2 // Threshold to overwrite

    for (let i = 0; i < params.activationCount; i++) {
      const currentAxonIndex = activationAttr.axonIndex.array[i]
      const currentIntensity = activationAttr.intensity.array[i]

      // Use first available slot
      if (currentAxonIndex < 0) {
        slotIndex = i
        break
      }
      // Or find lowest intensity pulse to replace
      else if (currentIntensity < lowestIntensity) {
        slotIndex = i
        lowestIntensity = currentIntensity
      }
    }

    // If we found a slot (we should always find one)
    if (slotIndex >= 0) {
      // Get the elapsed time since scene started to scale up intensity gradually
      const timeElapsed = clock.getElapsedTime()

      // Scale intensity during first 20 seconds (0.3x at start, ramping to full intensity)
      const intensityFactor = Math.min(1.0, timeElapsed / 20)

      // Apply more subtle intensity scaling
      const scaledIntensity = intensity * (0.3 + intensityFactor * 0.5) // Reduced maximum scaling (was 0.7)

      // More subtle pulses
      activationAttr.axonIndex.array[slotIndex] = axonIndex
      activationAttr.intensity.array[slotIndex] = Math.min(
        1.8, // Reduced maximum (was 3.0)
        scaledIntensity * 2.0, // Lower multiplier (was 3.0)
      )
      activationAttr.progress.array[slotIndex] =
        direction > 0.5 ? startProgress : 1 - startProgress
      activationAttr.direction.array[slotIndex] = direction
      // Smaller sizes for elegance
      activationAttr.size.array[slotIndex] = 1.8 + Math.random() * 1.2 // Reduced (was 2.5 + random*2.0)

      // Update the shader-based pulse visualization with more moderate values
      if (window.axonPulseInfo && axonIndex >= 0) {
        const idx = axonIndex * 4
        window.axonPulseInfo[idx] = 1.0 // Mark as active
        window.axonPulseInfo[idx + 1] =
          direction > 0.5 ? startProgress : 1 - startProgress
        window.axonPulseInfo[idx + 2] = Math.min(1.5, intensity * 1.6) // Lower intensity (was 2.5/2.5)
        window.axonPulseInfo[idx + 3] = direction

        // Immediately update the shader uniforms
        if (window.visibleAxons?.material?.uniforms) {
          window.visibleAxons.material.uniforms.pulsePositions.needsUpdate = true
        }
      }

      // More subtle connection strengthening
      if (axonStrengths[axonIndex] !== undefined) {
        // More subtle multiplier
        axonStrengths[axonIndex] = Math.min(2.0, axonStrengths[axonIndex] * 1.4) // Reduced maximum and multiplier (was 3.0/1.8)
        lastUsedTime[axonIndex] = clock.getElapsedTime()

        // Apply visible enhancement to the axon immediately
        updateAxonInstancePositions()

        // Also update connected neurons' strengths more subtly
        const sourceIdx = neuronIndices[axonIndex * 2]
        const targetIdx = neuronIndices[axonIndex * 2 + 1]

        if (
          sourceIdx >= 0 &&
          sourceIdx < params.particleCount &&
          targetIdx >= 0 &&
          targetIdx < params.particleCount
        ) {
          // Slightly increase the neuron strength with smaller boost
          neuronStrengths[sourceIdx] = Math.min(
            1.5, // Lower maximum (was 2.0)
            neuronStrengths[sourceIdx] * 1.03, // More subtle growth (was 1.05)
          )
          neuronStrengths[targetIdx] = Math.min(
            1.5, // Lower maximum
            neuronStrengths[targetIdx] * 1.03, // More subtle growth
          )
        }
      }

      // Log pulse creation with throttling - only in debug verbose mode
      if (debug.pulseLogs && debug.verbose) {
        debugLog(
          "pulse",
          `Created ${direction > 0.5 ? "forward" : "backward"} pulse on axon ${axonIndex} with intensity ${
            activationAttr.intensity.array[slotIndex]
          }`,
        )
      }
    }

    // Mark all attributes as needing update
    Object.keys(activationAttr).forEach((key) => {
      if (activationAttr[key].needsUpdate !== undefined) {
        activationAttr[key].needsUpdate = true
      }
    })
  }

  function updateActivations(time, deltaTime) {
    const attr = activations.geometry.attributes
    const progress = attr.progress.array
    const axonIndices = attr.axonIndex.array
    const directions = attr.direction.array
    const intensities = attr.intensity.array

    // Use a more consistent speed for better visibility of pulse travel
    const baseSpeed = params.activationSpeed // Use parameter for control

    // Reset all axon pulse info for this frame
    if (window.axonPulseInfo) {
      for (let i = 0; i < params.axonCount * 4; i += 4) {
        window.axonPulseInfo[i] = -1 // Mark all pulses as inactive initially
      }
    }

    // Update active pulses with improved validation
    let activeCount = 0
    for (let i = 0; i < params.activationCount; i++) {
      // Enhanced bound check, ensure it's a valid number and in range
      if (
        isNaN(axonIndices[i]) ||
        axonIndices[i] < 0 ||
        axonIndices[i] >= params.axonCount
      ) {
        // Invalidate any out-of-range axon indices
        if (axonIndices[i] >= 0) {
          console.warn(`Fixed out-of-range axon index: ${axonIndices[i]}`)
          axonIndices[i] = -1 // Mark as inactive
        }
        continue
      }

      activeCount++
      const axonIdx = Math.floor(axonIndices[i]) // Ensure it's an integer

      // Safe access check for neuronIndices
      const sourceNeuronIdx =
        axonIdx * 2 < neuronIndices.length ? neuronIndices[axonIdx * 2] : -1
      const targetNeuronIdx =
        axonIdx * 2 + 1 < neuronIndices.length
          ? neuronIndices[axonIdx * 2 + 1]
          : -1

      // Skip if invalid neuron indices
      if (
        sourceNeuronIdx < 0 ||
        targetNeuronIdx < 0 ||
        sourceNeuronIdx >= params.particleCount ||
        targetNeuronIdx >= params.particleCount
      ) {
        // Deactivate invalid axon references
        axonIndices[i] = -1
        continue
      }

      // MUCH SLOWER speed for better visibility of pulse travel
      const speed = baseSpeed * (0.65 + intensities[i] * 0.2) // Reduced and more consistent

      // Update progress based on direction
      if (directions[i] > 0.5) {
        // Moving forward
        progress[i] += speed * deltaTime

        // When reaching the end with safer index access
        if (progress[i] >= 0.95) {
          // Arriving at target neuron - trigger with higher intensity
          const neuronIdx = neuronIndices[axonIdx * 2 + 1]
          if (neuronIdx >= 0 && neuronIdx < params.particleCount) {
            // Stronger activation when pulse arrives
            triggerNeuronActivation(neuronIdx, intensities[i] * 1.2)
          }

          // Deactivate this pulse gradually
          intensities[i] *= 0.7

          // When fully at end, deactivate
          if (progress[i] >= 1.0) {
            axonIndices[i] = -1
          }
        }
      } else {
        // Moving backward
        progress[i] -= speed * deltaTime

        if (progress[i] <= 0.05) {
          // Arriving at source neuron - trigger with higher intensity
          const neuronIdx = neuronIndices[axonIdx * 2]
          if (neuronIdx >= 0 && neuronIdx < params.particleCount) {
            // Stronger activation when pulse arrives
            triggerNeuronActivation(neuronIdx, intensities[i] * 1.2)
          }

          intensities[i] *= 0.7

          if (progress[i] <= 0.0) {
            axonIndices[i] = -1
          }
        }
      }

      // Much slower fade for persistent visibility
      if (intensities[i] > 0) {
        intensities[i] *= 0.9995 // Almost no fade (was 0.9985)
      }

      // IMPORTANT: Update the axon pulse visualization info
      // This connects our point-based activations to the shader on the axons
      if (window.axonPulseInfo && axonIdx >= 0 && axonIdx < params.axonCount) {
        // Each axon has 4 values: [active, progress, intensity, direction]
        const idx = axonIdx * 4
        window.axonPulseInfo[idx] = 1.0 // Mark as active
        window.axonPulseInfo[idx + 1] = progress[i]
        window.axonPulseInfo[idx + 2] = intensities[i]
        window.axonPulseInfo[idx + 3] = directions[i]
      }
    }

    // Log active count occasionally with proper throttling
    const now = performance.now()
    if (
      debug.enabled &&
      now - debug.lastNetworkStatusTime > debug.statusLogInterval
    ) {
      debugLog("network", `Active pulses: ${activeCount}`, true)
      debug.lastNetworkStatusTime = now
    }

    // Update buffers
    attr.progress.needsUpdate = true
    attr.intensity.needsUpdate = true
    attr.axonIndex.needsUpdate = true

    // Update axon pulse visualization
    if (window.visibleAxons?.material?.uniforms) {
      window.visibleAxons.material.uniforms.pulsePositions.value =
        window.axonPulseInfo
      window.visibleAxons.material.uniforms.pulsePositions.needsUpdate = true
    }
  }

  // Adjust the neuron shader to respond to activations
  function updateNeuronShaderUniforms() {
    // Create a Float32Array to pass active neurons to shader
    if (!uniforms.activeNeurons) {
      uniforms.activeNeurons = {
        value: new Float32Array(params.particleCount).fill(0),
      }
    }

    // Reset all values
    const activeNeuronData = uniforms.activeNeurons.value
    for (let i = 0; i < params.particleCount; i++) {
      activeNeuronData[i] = 0
    }

    // Update uniforms to reflect axon strengths
    if (uniforms.neuronStrengths) {
      for (let i = 0; i < params.particleCount; i++) {
        uniforms.neuronStrengths.value[i] = 1.0 // Default strength

        // Find strongest connection for this neuron
        for (let j = 0; j < neuronIndices.length / 2; j++) {
          if (neuronIndices[j * 2] === i || neuronIndices[j * 2 + 1] === i) {
            if (!deadAxons.has(j) && axonStrengths[j] > 0.2) {
              uniforms.neuronStrengths.value[i] = Math.max(
                uniforms.neuronStrengths.value[i],
                axonStrengths[j],
              )
            }
          }
        }
      }

      uniforms.neuronStrengths.needsUpdate = true
    }

    // Update activeNeurons array with current activation values
    // FIXED: Use the existing activeNeuronData instead of declaring a new one
    // Copy activation values from the activeNeurons Map
    activeNeurons.forEach((data, neuronIndex) => {
      if (neuronIndex < params.particleCount) {
        activeNeuronData[neuronIndex] = data.intensity
      }
    })

    // Update neuron sphere opacity based on activation
    // This makes neurons become more translucent when activated
    if (neuronSpheres?.material) {
      // Check if we need to update individual colors for instances
      if (!neuronSpheres.instanceColor) {
        // Create instance colors if needed
        const colors = new Float32Array(params.particleCount * 3)
        for (let i = 0; i < params.particleCount; i++) {
          const baseColor = new THREE.Color(params.neuronColor)
          colors[i * 3] = baseColor.r
          colors[i * 3 + 1] = baseColor.g
          colors[i * 3 + 2] = baseColor.b
        }
        neuronSpheres.instanceColor = new THREE.InstancedBufferAttribute(
          colors,
          3,
        )
      }

      // Update each neuron's color/opacity based on activation with more subtle transitions
      for (let i = 0; i < params.particleCount; i++) {
        const activation = activeNeuronData[i]

        if (activation > 0.1) {
          // When activated, make neuron more translucent and brighter
          // This is achieved by changing color to a brighter variant
          // The additive blending will make it appear more translucent
          const color = new THREE.Color(params.neuronColor)
          // More subtle color boost for activated neurons
          color.r = Math.min(1.0, color.r + activation * 0.3) // Reduced from 0.4
          color.g = Math.min(1.0, color.g + activation * 0.35) // Reduced from 0.5
          color.b = Math.min(1.0, color.b + activation * 0.4) // Reduced from 0.6

          neuronSpheres.setColorAt(i, color)
        } else {
          // Default color when not activated
          neuronSpheres.setColorAt(i, new THREE.Color(params.neuronColor))
        }
      }

      // Update the instance colors
      if (neuronSpheres.instanceColor) {
        neuronSpheres.instanceColor.needsUpdate = true
      }
    }
  }

  // New function to manage axon strength over time
  function updateAxonStrengths(time, deltaTime) {
    // Update axon strengths - decay unused axons
    for (let i = 0; i < axonStrengths.length; i++) {
      // Skip already dead axons
      if (deadAxons.has(i)) continue

      const strength = axonStrengths[i]
      const timeSinceUsed = time - lastUsedTime[i]

      // Reduce strength for unused axons
      if (timeSinceUsed > 5.0) {
        axonStrengths[i] *= Math.pow(
          params.axonStrengthDecayRate,
          deltaTime * 60,
        )
      }

      // Mark dead axons
      if (axonStrengths[i] < params.axonDeathThreshold) {
        deadAxons.add(i)
      }
    }

    // Update neuron strengths based on their connections
    if (uniforms.neuronStrengths) {
      for (let i = 0; i < params.particleCount; i++) {
        uniforms.neuronStrengths.value[i] = 1.0 // Default strength

        // Find strongest connection for this neuron
        for (let j = 0; j < neuronIndices.length / 2; j++) {
          if (neuronIndices[j * 2] === i || neuronIndices[j * 2 + 1] === i) {
            if (!deadAxons.has(j) && axonStrengths[j] > 0.2) {
              uniforms.neuronStrengths.value[i] = Math.max(
                uniforms.neuronStrengths.value[i],
                axonStrengths[j],
              )
            }
          }
        }
      }

      uniforms.neuronStrengths.needsUpdate = true
    }

    // Occasionally regenerate dead connections if enabled
    if (
      params.regenerateConnections &&
      Math.random() < params.neuronRegenerationRate * deltaTime * 60
    ) {
      // Find a dead axon to regenerate
      if (deadAxons.size > 0) {
        const deadAxonsArray = Array.from(deadAxons)
        const axonToRegenerate =
          deadAxonsArray[Math.floor(Math.random() * deadAxonsArray.length)]

        // Create new connection between random neurons
        const neuron1 = Math.floor(Math.random() * params.particleCount)
        const neuron2 = Math.floor(Math.random() * params.particleCount)

        if (neuron1 !== neuron2) {
          // Update indices
          neuronIndices[axonToRegenerate * 2] = neuron1
          neuronIndices[axonToRegenerate * 2 + 1] = neuron2

          // Reset strength
          axonStrengths[axonToRegenerate] = 0.4 + Math.random() * 0.3

          // Remove from dead set
          deadAxons.delete(axonToRegenerate)

          // Update usage time
          lastUsedTime[axonToRegenerate] = time
        }
      }
    }
  }

  // Completely revamped activation cycle to create waves with quiet periods
  function startActivationCycle() {
    // Keep track of wave state
    let inActivationWave = false
    let waveTimer = 0
    let waveCooldown = 0

    // Create sporadic activation bursts followed by longer quiet periods
    const intervalId = setInterval(() => {
      const currentActiveCount = activeNeurons.size

      // If we're in an activation wave and activity has died down or wave has been active too long
      if (inActivationWave && (currentActiveCount < 3 || waveTimer > 4)) {
        // End the wave and enter quiet period with longer cooldown
        inActivationWave = false
        waveTimer = 0
        waveCooldown = 5 + Math.floor(Math.random() * 4) // 5-8 second cooldown (was 3-5)
        debugLog("wave", "Entering quiet period", debug.verbose)
      }
      // If we're not in a wave but in cooldown
      else if (!inActivationWave && waveCooldown > 0) {
        waveCooldown--
      }
      // If we're in a quiet period and cooldown is over - lower chance of activation
      else if (!inActivationWave && waveCooldown === 0 && Math.random() < 0.4) {
        // Reduced from 0.6
        // Start a new activation wave with more subtle burst of activity
        inActivationWave = true
        waveTimer = 0

        // Create a FOCUSED burst of activations - prefer clusters for more elegance
        const pattern = Math.random() < 0.8 ? "cluster" : "line" // Prefer clusters (was 0.7)
        const burstSize = Math.floor(Math.random() * 2) + 2 // 2-3 neurons (was 3-5)

        if (pattern === "cluster") {
          // Pick a random center neuron
          const centerIdx = Math.floor(Math.random() * params.particleCount)
          const centerPos = new THREE.Vector3(
            neuronPositions[centerIdx * 3],
            neuronPositions[centerIdx * 3 + 1],
            neuronPositions[centerIdx * 3 + 2],
          )

          // Activate with moderate intensity
          triggerNeuronActivation(centerIdx, 1.8) // Lower intensity (was 2.5)

          // Find and activate nearest neurons to create a cluster
          const nearbyNeurons = []

          // Find nearby neurons for cluster pattern
          for (let i = 0; i < params.particleCount; i++) {
            if (i === centerIdx) continue // Skip the center neuron

            const neuronPos = new THREE.Vector3(
              neuronPositions[i * 3],
              neuronPositions[i * 3 + 1],
              neuronPositions[i * 3 + 2],
            )

            const distance = neuronPos.distanceTo(centerPos)

            // Only include neurons within a reasonable cluster radius
            if (distance < 30) {
              nearbyNeurons.push({ index: i, distance: distance })
            }
          }

          // Sort by distance and take closest
          nearbyNeurons.sort((a, b) => a.distance - b.distance)

          // Activate nearest neurons with more subtlety
          for (
            let i = 0;
            i < Math.min(burstSize - 1, nearbyNeurons.length);
            i++
          ) {
            const delayMs = i * 150 // Slower, more elegant timing (was 100)
            setTimeout(() => {
              triggerNeuronActivation(nearbyNeurons[i].index, 1.6 - i * 0.3) // Lower intensity (was 2.2)
            }, delayMs)
          }

          debugLog(
            "wave",
            `Starting subtle cluster wave around neuron ${centerIdx}`,
            debug.verbose,
          )
        } else {
          // Line pattern with more grace

          // Choose a random starting neuron
          const startIdx = Math.floor(Math.random() * params.particleCount)
          const startPos = new THREE.Vector3(
            neuronPositions[startIdx * 3],
            neuronPositions[startIdx * 3 + 1],
            neuronPositions[startIdx * 3 + 2],
          )

          // Choose a random direction vector
          const direction = new THREE.Vector3(
            Math.random() * 2 - 1,
            Math.random() * 2 - 1,
            Math.random() * 2 - 1,
          ).normalize()

          // Find neurons along this direction line
          const lineNeurons = []

          for (let i = 0; i < params.particleCount; i++) {
            if (i === startIdx) continue // Skip starting neuron

            const neuronPos = new THREE.Vector3(
              neuronPositions[i * 3],
              neuronPositions[i * 3 + 1],
              neuronPositions[i * 3 + 2],
            )

            // Calculate vector from start to this neuron
            const toNeuron = neuronPos.clone().sub(startPos)
            const distance = toNeuron.length()

            // Check if this neuron is roughly along our chosen direction
            // Dot product tells us how aligned the vectors are
            const alignment = toNeuron.normalize().dot(direction)

            // Only include neurons that are reasonably aligned with our direction
            // and within a reasonable distance
            if (alignment > 0.7 && distance < 60) {
              lineNeurons.push({
                index: i,
                distance: distance,
                alignment: alignment,
              })
            }
          }

          // Sort neurons by distance from starting point
          lineNeurons.sort((a, b) => a.distance - b.distance)

          // Activate in sequence with more subtlety
          triggerNeuronActivation(startIdx, 1.7) // Lower intensity (was 2.5)

          for (
            let i = 0;
            i < Math.min(burstSize - 1, lineNeurons.length);
            i++
          ) {
            const delayMs = (i + 1) * 200 // Slower, more elegant delays (was 150)
            setTimeout(() => {
              triggerNeuronActivation(lineNeurons[i].index, 1.5 - i * 0.15) // Lower intensity (was 2.2)
            }, delayMs)
          }

          debugLog(
            "wave",
            `Starting subtle line wave from neuron ${startIdx}`,
            debug.verbose,
          )
        }
      }
      // When in wave, occasionally add more activations but with lower frequency
      else if (inActivationWave && Math.random() < 0.25) {
        // Reduced from 0.4
        // Find currently active neurons
        const activeIndices = Array.from(activeNeurons.keys())

        if (activeIndices.length > 0) {
          // Pick a random active neuron and boost it with moderate intensity
          const sourceIdx =
            activeIndices[Math.floor(Math.random() * activeIndices.length)]
          triggerNeuronActivation(sourceIdx, 1.4) // Lower intensity (was 1.9)
        }
      }

      // Increment wave timer if we're in a wave
      if (inActivationWave) {
        waveTimer += 1
      }
    }, 1200) // Check less frequently (was 1000ms)

    // Store interval for cleanup
    window.activationInterval = intervalId
  }

  // Additional function to enforce network connectivity
  function ensureFullConnectivity() {
    // Use the neuronIndices to verify all neurons are connected
    const connectedNeurons = new Set()

    // Traverse all the neuron indices to track which ones have connections
    for (let i = 0; i < neuronIndices.length; i += 2) {
      const sourceIdx = neuronIndices[i]
      const targetIdx = neuronIndices[i + 1]

      if (sourceIdx >= 0 && sourceIdx < params.particleCount) {
        connectedNeurons.add(sourceIdx)
      }

      if (targetIdx >= 0 && targetIdx < params.particleCount) {
        connectedNeurons.add(targetIdx)
      }
    }

    // Verify we have full connectivity
    if (connectedNeurons.size < params.particleCount) {
      console.warn(
        `Network connectivity check: ${params.particleCount - connectedNeurons.size} neurons are still disconnected!`,
      )

      // Find and fix any remaining disconnected neurons
      for (let i = 0; i < params.particleCount; i++) {
        if (!connectedNeurons.has(i)) {
          debugLog(
            "network",
            `Found disconnected neuron: ${i} - will connect on next regeneration cycle`,
            true,
          )
        }
      }
    } else {
      debugLog(
        "network",
        `Network connectivity verified: All ${params.particleCount} neurons are connected`,
        true,
      )
    }
  }

  // Modified to create sporadic activation waves rather than constant activity
  function initializeActivations() {
    debugLog("startup", "Initializing neural network activations...", true)

    // Reduced initial activations (8 instead of 16)
    // This creates fewer starting points for clearer branching patterns
    for (let i = 0; i < 8; i++) {
      const neuronIndex = Math.floor(Math.random() * params.particleCount)
      triggerNeuronActivation(neuronIndex, 1.8) // Higher intensity for better visibility
      startPulsesFromNeuron(neuronIndex)
    }

    // Short burst of additional activations to simulate initial network activity
    setTimeout(() => {
      for (let i = 0; i < 4; i++) {
        const neuronIndex = Math.floor(Math.random() * params.particleCount)
        triggerNeuronActivation(neuronIndex, 1.6)
        startPulsesFromNeuron(neuronIndex)
      }
    }, 1000)

    // No continuous ramp-up, just start activation cycle after short delay
    setTimeout(() => {
      debugLog("startup", "Starting sporadic activation cycle", true)
      startActivationCycle()
    }, 3000)
  }

  onMount(() => {
    if (browser) {
      // Initialize THREE.js clock
      clock = new THREE.Clock()
      startTime = clock.getElapsedTime()

      // Wait a very short time for the container to be properly mounted
      setTimeout(() => {
        if (container) {
          // Simplified logging - only log once at initialization
          if (debug.showCriticalLogs) {
            console.log("Initializing neural network visualization")
          }

          initScene()
          initMouseInteraction()

          // Verify network connectivity with less verbose logging
          setTimeout(() => {
            ensureFullConnectivity()
          }, 1000)

          initializeActivations()
          window.particleEffects = createParticleEffects()
          animate()
        } else {
          console.error("Container element is not available")
        }
      }, 10) // Just a tiny delay to ensure DOM is ready
    }
  })

  onDestroy(() => {
    if (browser) {
      if (animationId) {
        cancelAnimationFrame(animationId)
      }

      window.removeEventListener("resize", onWindowResize)

      if (particles?.geometry) particles.geometry.dispose()
      if (particles?.material) particles.material.dispose()
      if (axons?.geometry) axons.geometry.dispose()
      if (axons?.material) axons.material.dispose()
      if (activations?.geometry) activations.geometry.dispose()
      if (activations?.material) activations.material.dispose()

      if (renderer) {
        renderer.dispose()
        if (container && container.contains(renderer.domElement)) {
          container.removeChild(renderer.domElement)
        }
      }

      // Clear the activation cycle interval
      if (window.activationInterval) {
        clearInterval(window.activationInterval)
      }

      document.removeEventListener("mousemove", onMouseMove)
    }
  })

  // Add mouse event handlers to document
  function initMouseInteraction() {
    if (browser) {
      // Create debug sphere for visualizing ray intersection
      const sphereGeometry = new THREE.SphereGeometry(2, 16, 16)
      const sphereMaterial = new THREE.MeshBasicMaterial({
        color: 0xff0000,
        transparent: true,
        opacity: 0.5,
      })
      mouseDebugSphere = new THREE.Mesh(sphereGeometry, sphereMaterial)
      if (scene) scene.add(mouseDebugSphere)
      mouseDebugSphere.visible = false // Hide initially, show only during interaction

      document.addEventListener("mousemove", onMouseMove)

      // Add a helper function to check if click is inside control panel
      function isClickInsideControlPanel(event) {
        // Find the control panel element
        const controlPanel = document.querySelector(".control-panel")

        // If the control panel isn't open, it can't be clicked
        if (!controlPanelOpen || !controlPanel) {
          return false
        }

        // Also check for the control toggle button
        const controlToggle = document.querySelector(".control-toggle")

        // Check if the click target is within either element
        return (
          controlPanel.contains(event.target) ||
          (controlToggle && controlToggle.contains(event.target))
        )
      }

      // Add a click handler that provides immediate feedback
      document.addEventListener("click", (event) => {
        // Skip processing if the click was inside the control panel
        if (isClickInsideControlPanel(event)) {
          return // Exit early to prevent neural network from responding to UI clicks
        }

        // Force immediate interaction regardless of cooldown
        mousePosition.x = (event.clientX / window.innerWidth) * 2 - 1
        mousePosition.y = -(event.clientY / window.innerHeight) * 2 + 1

        // Process with higher intensity on click
        lastMouseMoveTime = 0 // Reset cooldown
        mouseActivationEnabled = true // Ensure enabled
        processMouseIntersection(true, false) // Don't show debug in console
      })

      // Only log once, and only when critical logs are enabled
      if (debug.showCriticalLogs) {
        console.log("Mouse interaction initialized")
      }
    }
  }

  function onMouseMove(event) {
    // Update target mouse position normalized to -1 to 1
    targetMousePosition.x = (event.clientX / window.innerWidth) * 2 - 1
    targetMousePosition.y = -(event.clientY / window.innerHeight) * 2 + 1

    // Don't process every mouse move to avoid overwhelming the system
    const now = performance.now()
    if (now - lastMouseMoveTime < mouseMoveCooldown) return
    lastMouseMoveTime = now

    // Only proceed if mouse interaction is enabled
    if (!mouseActivationEnabled) return

    // Process the mouse position with smoothing in the animation loop
    // Actual ray casting happens in the animate function for smoother results
  }

  // The function that actually processes mouse intersection
  // Added quietMode parameter to suppress logs
  function processMouseIntersection(isClick = false, showDebug = false) {
    // Skip processing if camera or scene isn't initialized
    if (!camera || !scene) return

    // Update the raycaster with current mouse position
    raycaster.setFromCamera(mousePosition, camera)

    // Use a simpler approach - cast ray into scene and check distance to neurons
    const rayDirection = new THREE.Vector3()
    raycaster.ray.direction.normalize()

    // Create fixed distance reference point along ray
    const intersectionDistance = camera.position.z * 0.8
    const intersectionPoint = new THREE.Vector3()
    intersectionPoint
      .copy(raycaster.ray.origin)
      .add(raycaster.ray.direction.clone().multiplyScalar(intersectionDistance))

    // Show debug sphere at intersection point if debugging or click
    if (mouseDebugSphere) {
      mouseDebugSphere.position.copy(intersectionPoint)
      mouseDebugSphere.visible = isClick // Show on click

      // Hide debug sphere after 1 second
      if (isClick) {
        setTimeout(() => {
          if (mouseDebugSphere) mouseDebugSphere.visible = false
        }, 1000)
      }
    }

    let closestDistance = mouseActivationRadius
    let closestNeuron = null

    // Check all neurons for proximity to ray intersection point
    for (let i = 0; i < params.particleCount; i++) {
      const neuronPos = new THREE.Vector3(
        neuronPositions[i * 3],
        neuronPositions[i * 3 + 1],
        neuronPositions[i * 3 + 2],
      )

      // Calculate distance to intersection point
      const distance = neuronPos.distanceTo(intersectionPoint)

      if (distance < closestDistance) {
        closestDistance = distance
        closestNeuron = i
      }
    }

    // If we found a neuron close to the mouse ray, activate it
    if (closestNeuron !== null) {
      const now = performance.now()

      // Check if this is a new neuron or a click
      if (isClick) {
        // Only log in debug mode and when showDebug is true
        if (debug.enabled && showDebug) {
          debugLog(
            "interaction",
            `Mouse activating neuron ${closestNeuron} - distance: ${closestDistance.toFixed(2)}`,
            true,
          )
        }

        // On click, use a random pattern for more dramatic effect
        triggerPatternActivation(closestNeuron, mouseHoverIntensity * 1.2)
        highlightNeuron(closestNeuron, true)

        // Update hover state to prevent immediate hover effect after click
        currentHoveredNeuron = closestNeuron
        hoverStartTime = now
      }
      // Smooth hover transition when a new neuron is hovered
      else if (closestNeuron !== currentHoveredNeuron) {
        // Fade out previous hover if there was one
        if (currentHoveredNeuron !== null) {
          fadeOutHover(currentHoveredNeuron)
        }

        // Start hover on new neuron
        currentHoveredNeuron = closestNeuron
        hoverStartTime = now

        // Begin smooth intensity ramp-up
        startSmoothHover(closestNeuron)
      }
      // Continue hovering the same neuron - already handled by the smooth hover system
    }
    // If no neuron is under mouse, fade out any previous hover
    else if (currentHoveredNeuron !== null) {
      fadeOutHover(currentHoveredNeuron)
      currentHoveredNeuron = null
    }
  }

  // New function to start a smooth hover effect
  function startSmoothHover(neuronIndex) {
    // Initial light activation to ensure neuron is visible immediately
    const initialIntensity = 0.8

    // Start with lighter activation
    triggerNeuronActivation(neuronIndex, initialIntensity)
    highlightNeuron(neuronIndex, false, true)

    // Schedule gradual increase in intensity
    startPulsesFromNeuron(neuronIndex, 0.6)
  }

  // New function to smoothly fade out hover effect
  function fadeOutHover(neuronIndex) {
    // The activeNeurons map and shader uniforms will handle the natural decay
    // We don't need to do anything special here, as the neuron will naturally
    // fade out due to the decay rate
  }

  // Updated function for neuron highlighting with smoother transitions
  function highlightNeuron(neuronIndex, isClick, isHover = false) {
    if (neuronIndex < 0 || neuronIndex >= params.particleCount) return

    // Make this neuron glow brighter temporarily
    if (uniforms.activeNeurons?.value) {
      let intensity

      if (isClick) {
        // Stronger effect for clicks
        intensity = 4.0
      } else if (isHover) {
        // For hover, start with a milder effect that will increase over time
        intensity = 1.2
      } else {
        // Default intensity
        intensity = 2.5
      }

      uniforms.activeNeurons.value[neuronIndex] = intensity

      // Reset after a delay (faster for hover, longer for click)
      if (isClick) {
        setTimeout(() => {
          // Don't reset if it became activated through normal network activity
          const activeData = activeNeurons.get(neuronIndex)
          if (!activeData || activeData.intensity < 0.5) {
            if (uniforms.activeNeurons?.value) {
              uniforms.activeNeurons.value[neuronIndex] = 0
            }
          }
        }, 1500)
      }
      // For hover effects, we let the natural decay handle it
    }
  }

  function triggerPatternActivation(neuronIndex, intensity) {
    // Select a random pattern based on weighted chances
    const random = Math.random()
    let cumulativeChance = 0
    let selectedPattern = activationPatterns[0] // Default to first pattern

    for (const pattern of activationPatterns) {
      cumulativeChance += pattern.chance
      if (random <= cumulativeChance) {
        selectedPattern = pattern
        break
      }
    }

    // Apply the selected pattern
    const finalIntensity = intensity * selectedPattern.intensityFactor
    debugLog(
      "pattern",
      `Triggering ${selectedPattern.name} pattern from neuron ${neuronIndex} with intensity ${finalIntensity.toFixed(2)}`,
      debug.verbose,
    )

    switch (selectedPattern.name) {
      case "ripple":
        // Ripple: Activate in concentric waves
        triggerRippleActivation(neuronIndex, finalIntensity)
        break
      case "cascade":
        // Cascade: Sequential activation along connected paths
        triggerCascadeActivation(neuronIndex, finalIntensity)
        break
      case "pulse":
        // Pulse: Strong single activation with few branches
        triggerPulseActivation(neuronIndex, finalIntensity)
        break
      case "burst":
        // Burst: Many simultaneous activations in all directions
        triggerBurstActivation(neuronIndex, finalIntensity)
        break
      case "sparkle":
        // Sparkle: Several disconnected activations that quickly fade
        triggerSparkleActivation(neuronIndex, finalIntensity)
        break
      default:
        // Default: Regular activation
        triggerNeuronActivation(neuronIndex, finalIntensity)
        break
    }
  }

  // Pattern implementations
  function triggerRippleActivation(neuronIndex, intensity) {
    // First activate the center neuron
    triggerNeuronActivation(neuronIndex, intensity)

    // Create concentric waves of activation
    const centerPos = new THREE.Vector3(
      neuronPositions[neuronIndex * 3],
      neuronPositions[neuronIndex * 3 + 1],
      neuronPositions[neuronIndex * 3 + 2],
    )

    const waveCount = 2 + Math.floor(Math.random() * 2) // 2-3 waves

    for (let wave = 1; wave <= waveCount; wave++) {
      // Calculate radius for this wave
      const minRadius = wave * 10
      const maxRadius = minRadius + 15

      // Neurons in this wave
      const waveNeurons = []

      // Find neurons in this wave's distance band
      for (let i = 0; i < params.particleCount; i++) {
        if (i === neuronIndex) continue // Skip center neuron

        const neuronPos = new THREE.Vector3(
          neuronPositions[i * 3],
          neuronPositions[i * 3 + 1],
          neuronPositions[i * 3 + 2],
        )

        const dist = neuronPos.distanceTo(centerPos)

        if (dist >= minRadius && dist < maxRadius) {
          waveNeurons.push({ index: i, distance: dist })
        }
      }

      // Activate a subset of neurons in this wave with delays
      const neuronsToActivate = Math.min(
        3 + Math.floor(Math.random() * 4), // 3-6 neurons per wave
        waveNeurons.length,
      )

      for (let i = 0; i < neuronsToActivate; i++) {
        if (waveNeurons.length === 0) break

        // Pick a random neuron from this wave
        const randomIdx = Math.floor(Math.random() * waveNeurons.length)
        const neuron = waveNeurons.splice(randomIdx, 1)[0]

        // Activate with delay proportional to wave number
        const delay = wave * 120 + Math.random() * 50
        const waveIntensity = intensity * (1 - (wave - 1) * 0.2)

        setTimeout(() => {
          triggerNeuronActivation(neuron.index, waveIntensity)
        }, delay)
      }
    }
  }

  function triggerCascadeActivation(neuronIndex, intensity) {
    // Find a chain of connections to follow
    const visited = new Set()
    const toVisit = [{ neuron: neuronIndex, depth: 0, intensity }]
    visited.add(neuronIndex)

    const maxDepth = 3 + Math.floor(Math.random() * 3) // 3-5 steps in cascade

    // Start the cascade with the first neuron
    triggerNeuronActivation(neuronIndex, intensity)

    // Process each neuron in the cascade
    while (toVisit.length > 0) {
      const current = toVisit.shift()

      // Skip if we've reached max depth
      if (current.depth >= maxDepth) continue

      // Find connected neurons
      const connectedNeurons = []

      // Search for connections in both directions
      for (let i = 0; i < params.axonCount; i++) {
        if (deadAxons.has(i)) continue

        const sourceIdx = neuronIndices[i * 2]
        const targetIdx = neuronIndices[i * 2 + 1]

        if (sourceIdx === current.neuron && !visited.has(targetIdx)) {
          connectedNeurons.push({
            index: targetIdx,
            connectionIdx: i,
            isOutgoing: true,
          })
        } else if (targetIdx === current.neuron && !visited.has(sourceIdx)) {
          connectedNeurons.push({
            index: sourceIdx,
            connectionIdx: i,
            isOutgoing: false,
          })
        }
      }

      // Pick 1-2 connections to continue the cascade
      const connectionsToFollow = Math.min(
        1 + Math.floor(Math.random() * 2),
        connectedNeurons.length,
      )

      for (let i = 0; i < connectionsToFollow; i++) {
        if (connectedNeurons.length === 0) break

        // Pick a random connected neuron
        const randomIdx = Math.floor(Math.random() * connectedNeurons.length)
        const nextNeuron = connectedNeurons.splice(randomIdx, 1)[0]

        // Mark as visited
        visited.add(nextNeuron.index)

        // Calculate new intensity - decreases with depth
        const newIntensity = current.intensity * 0.8

        // Delay for cascade effect
        const delay = (current.depth + 1) * 150

        // Schedule activation
        setTimeout(() => {
          // Create pulse on connection
          createPulseOnAxon(
            nextNeuron.connectionIdx,
            nextNeuron.isOutgoing ? 0 : 1,
            nextNeuron.isOutgoing ? 1 : 0,
            newIntensity,
          )

          // Activate the neuron after a slight delay for the pulse to travel
          setTimeout(() => {
            triggerNeuronActivation(nextNeuron.index, newIntensity)
          }, 50)
        }, delay)

        // Add to visit queue
        toVisit.push({
          neuron: nextNeuron.index,
          depth: current.depth + 1,
          intensity: newIntensity,
        })
      }
    }
  }

  function triggerPulseActivation(neuronIndex, intensity) {
    // Strong single activation
    triggerNeuronActivation(neuronIndex, intensity * 1.5)
    // Then find all direct connections and activate them rapidly
    // Minimal branching - just 1-2 connections
    setTimeout(() => {
      startPulsesFromNeuron(neuronIndex, intensity * 1.3)
    }, 50)
  }

  function triggerBurstActivation(neuronIndex, intensity) {
    // First, trigger the central neuron
    triggerNeuronActivation(neuronIndex, intensity)

    // Then find all direct connections and activate them rapidly
    const connections = []

    // Get all direct connections
    for (let i = 0; i < params.axonCount; i++) {
      if (deadAxons.has(i)) continue

      const sourceIdx = neuronIndices[i * 2]
      const targetIdx = neuronIndices[i * 2 + 1]

      if (sourceIdx === neuronIndex) {
        connections.push({
          neuron: targetIdx,
          connection: i,
          isOutgoing: true,
        })
      } else if (targetIdx === neuronIndex) {
        connections.push({
          neuron: sourceIdx,
          connection: i,
          isOutgoing: false,
        })
      }
    }

    // Randomly select a subset of connections for burst
    const burstSize = Math.min(
      5 + Math.floor(Math.random() * 5), // 5-9 connections
      connections.length,
    )

    // Shuffle connections array
    for (let i = connections.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[connections[i], connections[j]] = [connections[j], connections[i]]
    }

    // Activate selected connections with minimal delays for burst effect
    for (let i = 0; i < burstSize; i++) {
      const conn = connections[i]

      // Very short staggered delays
      const delay = 30 + Math.random() * 40

      setTimeout(() => {
        // Create pulse
        createPulseOnAxon(
          conn.connection,
          conn.isOutgoing ? 0 : 1,
          conn.isOutgoing ? 1 : 0,
          intensity * 0.9,
        )

        // Activate target neuron after pulse
        setTimeout(() => {
          triggerNeuronActivation(conn.neuron, intensity * 0.85)
        }, 30)
      }, delay)
    }
  }

  function triggerSparkleActivation(neuronIndex, intensity) {
    // Activate the initial neuron
    triggerNeuronActivation(neuronIndex, intensity)

    // Find a set of random neurons within a certain radius to create a sparkle effect
    const centerPos = new THREE.Vector3(
      neuronPositions[neuronIndex * 3],
      neuronPositions[neuronIndex * 3 + 1],
      neuronPositions[neuronIndex * 3 + 2],
    )
    const maxRadius = 40
    const nearbyNeurons = []

    // Find neurons within radius
    for (let i = 0; i < params.particleCount; i++) {
      if (i === neuronIndex) continue

      const neuronPos = new THREE.Vector3(
        neuronPositions[i * 3],
        neuronPositions[i * 3 + 1],
        neuronPositions[i * 3 + 2],
      )

      const dist = neuronPos.distanceTo(centerPos)

      if (dist < maxRadius) {
        nearbyNeurons.push({ index: i, distance: dist })
      }
    }

    // Select a few neurons to sparkle
    const sparkleCount = Math.min(
      3 + Math.floor(Math.random() * 3), // 3-5 sparkles
      nearbyNeurons.length,
    )

    // Sort by distance
    nearbyNeurons.sort((a, b) => a.distance - b.distance)

    // Select neurons distributed throughout the radius
    const step = Math.floor(nearbyNeurons.length / sparkleCount)
    for (let i = 0; i < sparkleCount; i++) {
      const idx = Math.min(i * step, nearbyNeurons.length - 1)
      const neuron = nearbyNeurons[idx]

      // Random delay for twinkling effect
      const delay = 50 + Math.random() * 150

      // Intensity varies for sparkle effect
      const sparkleIntensity = intensity * (0.7 + Math.random() * 0.5)

      setTimeout(() => {
        triggerNeuronActivation(neuron.index, sparkleIntensity)

        // Quick decay for sparkle effect - make it briefly bright then fade quickly
        setTimeout(
          () => {
            // If the neuron is still active, reduce its intensity
            const data = activeNeurons.get(neuron.index)
            if (data) {
              data.intensity *= 0.5
            }
          },
          100 + Math.random() * 100,
        )
      }, delay)
    }
  }

  // Control panel state
  let controlPanelOpen = false
  let activeTab = "visual"

  // Make copies of parameters for control binding
  // These avoid direct mutation of the params object which might not be reactive
  let uiBloomStrength = params.bloomStrength
  let uiBloomRadius = params.bloomRadius
  let uiPulseIntensity = params.axonPulseIntensity
  let uiActivationSpeed = params.activationSpeed
  let uiPropagationChance = params.activationPropagationChance
  let uiDecayRate = params.activationDecayRate

  // Helper function to update shader parameters
  function updateShaderParams() {
    if (!browser) return // Skip if not in browser environment

    if (composer && params.usePostProcessing) {
      // Update bloom pass if it exists
      const bloomPass = composer.passes.find(
        (pass) => pass.name === "UnrealBloomPass",
      )
      if (bloomPass) {
        bloomPass.strength = params.bloomStrength
        bloomPass.radius = params.bloomRadius
        bloomPass.threshold = params.bloomThreshold
      }
    }

    // Update axon pulse intensity in shader
    if (window.visibleAxons?.material?.uniforms) {
      window.visibleAxons.material.uniforms.pulseIntensity.value =
        params.axonPulseIntensity
    }
  }

  // Update UI values when panel opens or tab changes
  function syncUIValues() {
    uiBloomStrength = params.bloomStrength
    uiBloomRadius = params.bloomRadius
    uiPulseIntensity = params.axonPulseIntensity
    uiActivationSpeed = params.activationSpeed
    uiPropagationChance = params.activationPropagationChance
    uiDecayRate = params.activationDecayRate
  }

  // Watch for control panel state changes
  $: if (controlPanelOpen || activeTab) {
    syncUIValues()
  }

  // Toggle control panel visibility
  function toggleControlPanel() {
    controlPanelOpen = !controlPanelOpen
    if (controlPanelOpen) {
      syncUIValues()
    }
  }

  // Change active tab
  function setActiveTab(tab: string) {
    activeTab = tab
    syncUIValues()
  }

  // Create reactivity between UI controls and actual parameters
  $: if (uiBloomStrength !== undefined && browser) {
    params.bloomStrength = uiBloomStrength
    updateShaderParams()
  }

  $: if (uiBloomRadius !== undefined && browser) {
    params.bloomRadius = uiBloomRadius
    updateShaderParams()
  }

  $: if (uiPulseIntensity !== undefined && browser) {
    params.axonPulseIntensity = uiPulseIntensity
    updateShaderParams()
  }

  $: if (uiActivationSpeed !== undefined) {
    params.activationSpeed = uiActivationSpeed
  }

  $: if (uiPropagationChance !== undefined) {
    params.activationPropagationChance = uiPropagationChance
  }

  $: if (uiDecayRate !== undefined) {
    params.activationDecayRate = uiDecayRate
  }

  // External link helper
  function openExternalLink(url: string) {
    if (browser) {
      window.open(url, "_blank")
    }
  }
</script>

<!-- Add a proper container element here with binding -->
<div bind:this={container} class="neural-network-background"></div>

<!-- Toggle button for control panel -->
<button
  class="control-toggle"
  on:click={toggleControlPanel}
  aria-label={controlPanelOpen ? "Close controls" : "Open controls"}
>
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    {#if controlPanelOpen}
      <!-- Down arrow -->
      <path d="M6 9l6 6 6-6" />
    {:else}
      <!-- Up arrow -->
      <path d="M6 15l6-6 6 6" />
    {/if}
  </svg>
</button>

<!-- Control panel -->
{#if controlPanelOpen}
  <div class="control-panel">
    <!-- Tabs -->
    <div class="control-tabs">
      <button
        class={activeTab === "visual" ? "active" : ""}
        on:click={() => setActiveTab("visual")}
      >
        Visual
      </button>
      <button
        class={activeTab === "animation" ? "active" : ""}
        on:click={() => setActiveTab("animation")}
      >
        Animation
      </button>
      <button
        class={activeTab === "interaction" ? "active" : ""}
        on:click={() => setActiveTab("interaction")}
      >
        Interaction
      </button>
      <button
        class={activeTab === "about" ? "active" : ""}
        on:click={() => setActiveTab("about")}
      >
        About
      </button>
    </div>

    <!-- Tab content -->
    <div class="tab-content">
      {#if activeTab === "visual"}
        <div class="tab-panel">
          <h3>Visual Settings</h3>
          <div class="control-row">
            <label for="bloom-strength">Bloom Strength</label>
            <input
              type="range"
              id="bloom-strength"
              min="0"
              max="2"
              step="0.05"
              bind:value={uiBloomStrength}
            />
            <span class="value-display">{uiBloomStrength.toFixed(2)}</span>
          </div>
          <div class="control-row">
            <label for="bloom-radius">Bloom Radius</label>
            <input
              type="range"
              id="bloom-radius"
              min="0"
              max="1"
              step="0.05"
              bind:value={uiBloomRadius}
            />
            <span class="value-display">{uiBloomRadius.toFixed(2)}</span>
          </div>
          <div class="control-row">
            <label for="pulse-intensity">Pulse Intensity</label>
            <input
              type="range"
              id="pulse-intensity"
              min="0.5"
              max="5"
              step="0.1"
              bind:value={uiPulseIntensity}
            />
            <span class="value-display">{uiPulseIntensity.toFixed(1)}</span>
          </div>
        </div>
      {:else if activeTab === "animation"}
        <div class="tab-panel">
          <h3>Animation Settings</h3>
          <div class="control-row">
            <label for="activation-speed">Pulse Speed</label>
            <input
              type="range"
              id="activation-speed"
              min="0.05"
              max="0.3"
              step="0.01"
              bind:value={uiActivationSpeed}
            />
            <span class="value-display">{uiActivationSpeed.toFixed(2)}</span>
          </div>
          <div class="control-row">
            <label for="propagation-chance">Propagation Chance</label>
            <input
              type="range"
              id="propagation-chance"
              min="0.5"
              max="1"
              step="0.05"
              bind:value={uiPropagationChance}
            />
            <span class="value-display">{uiPropagationChance.toFixed(2)}</span>
          </div>
          <div class="control-row">
            <label for="decay-rate">Decay Rate</label>
            <input
              type="range"
              id="decay-rate"
              min="0.9"
              max="0.99"
              step="0.01"
              bind:value={uiDecayRate}
            />
            <span class="value-display">{uiDecayRate.toFixed(2)}</span>
          </div>
        </div>
      {:else if activeTab === "interaction"}
        <div class="tab-panel">
          <h3>Interaction Settings</h3>
          <div class="control-row">
            <label for="mouse-intensity">Mouse Hover Intensity</label>
            <input
              type="range"
              id="mouse-intensity"
              min="1"
              max="5"
              step="0.5"
              bind:value={mouseHoverIntensity}
            />
            <span class="value-display">{mouseHoverIntensity.toFixed(1)}</span>
          </div>
          <div class="control-row toggle-row">
            <label for="mouse-activation">Mouse Activation</label>
            <div class="toggle-container">
              <label class="switch">
                <input
                  type="checkbox"
                  id="mouse-activation"
                  bind:checked={mouseActivationEnabled}
                />
                <span class="slider round"></span>
              </label>
              <span class="checkbox-label"
                >{mouseActivationEnabled ? "Enabled" : "Disabled"}</span
              >
            </div>
          </div>
        </div>
      {:else if activeTab === "about"}
        <div class="tab-panel about-panel">
          <div class="logo-container">
            <!-- Replace the img with a proper button for better accessibility -->
            <button
              class="logo-button"
              on:click={() => openExternalLink("https://hollas.net")}
              on:keydown={(e) =>
                e.key === "Enter" && openExternalLink("https://hollas.net")}
              aria-label="Visit Hollas website"
            >
              <img
                src="/images/hollas-logo.svg"
                alt="Hollas AB"
                onerror="this.onerror=null; this.src='/favicon.png';"
                class="hollas-logo"
              />
            </button>
          </div>
          <h3>Neural Network Background</h3>
          <p>
            This interactive neural network visualization represents the
            interconnected nature of AI systems and data processing.
          </p>
          <p>
            Developed by <a
              href="https://hollas.net"
              target="_blank"
              rel="noopener noreferrer">Hollas AB</a
            > © 2025
          </p>
          <div class="version">Version 1.0.0</div>
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .neural-network-background {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
    overflow: hidden;
    opacity: 0.85; /* Add slight overall opacity for subtlety */
    pointer-events: auto; /* Ensure mouse events are captured */
  }

  /* Control toggle button - made more subtle */
  .control-toggle {
    position: fixed;
    bottom: 16px;
    right: 16px;
    width: 30px; /* Reduced from 36px */
    height: 30px; /* Reduced from 36px */
    background: rgba(15, 25, 55, 0.4); /* Reduced opacity from 0.7 to 0.4 */
    border: 1px solid rgba(100, 140, 230, 0.25); /* Lighter border */
    color: rgba(180, 210, 255, 0.5); /* Reduced color opacity */
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
    transition: all 0.3s ease;
    box-shadow: 0 1px 4px rgba(0, 10, 30, 0.2); /* Reduced shadow */
    opacity: 0.6; /* Start with low opacity */
  }

  .control-toggle:hover {
    background: rgba(25, 35, 70, 0.7);
    color: rgba(200, 230, 255, 0.9);
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(0, 10, 30, 0.3);
    opacity: 1; /* Full opacity on hover */
  }

  .control-toggle svg {
    width: 14px; /* Smaller icon */
    height: 14px; /* Smaller icon */
    opacity: 0.8;
  }

  /* Fade in the control toggle after a delay */
  @keyframes fadeInButton {
    from {
      opacity: 0;
    }
    to {
      opacity: 0.6;
    }
  }

  .control-toggle {
    animation: fadeInButton 1.5s ease 3s forwards; /* Start after 3s, last 1.5s */
    opacity: 0; /* Start invisible */
  }

  /* Control panel */
  .control-panel {
    position: fixed;
    bottom: 70px;
    right: 20px;
    width: 330px; /* Increased from 320px to prevent horizontal scrolling */
    background: rgba(15, 25, 55, 0.85);
    border: 1px solid rgba(100, 140, 230, 0.4);
    color: rgba(220, 230, 255, 0.9);
    border-radius: 8px;
    overflow: hidden;
    z-index: 10;
    box-shadow: 0 4px 20px rgba(0, 10, 30, 0.5);
    animation: slideIn 0.3s ease;
    display: flex;
    flex-direction: column;
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Tabs */
  .control-tabs {
    display: flex;
    border-bottom: 1px solid rgba(100, 140, 230, 0.3);
    flex-wrap: nowrap; /* Ensure tabs don't wrap */
  }

  .control-tabs button {
    flex: 1;
    background: transparent;
    color: rgba(180, 210, 255, 0.7);
    border: none;
    padding: 10px 5px; /* Reduced horizontal padding from 10px 0 to 10px 5px */
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 11px; /* Reduced from 12px */
    white-space: nowrap;
    min-width: 70px; /* Ensure minimum width for readability */
  }

  .control-tabs button:hover {
    background: rgba(70, 100, 180, 0.2);
    color: rgba(200, 230, 255, 0.9);
  }

  .control-tabs button.active {
    background: rgba(70, 100, 180, 0.3);
    color: rgba(220, 240, 255, 1);
    border-bottom: 2px solid rgba(100, 160, 255, 0.8);
  }

  /* Tab content */
  .tab-content {
    padding: 15px;
    max-height: 300px;
    overflow-y: auto;
    flex-grow: 1;
    display: block;
    background-color: rgba(15, 25, 55, 0.85);
  }

  .tab-panel {
    display: block;
    width: 100%;
    color: rgba(220, 230, 255, 0.9);
  }

  .tab-panel h3 {
    margin-top: 0;
    margin-bottom: 12px;
    font-size: 16px;
    color: rgba(220, 240, 255, 0.9);
    font-weight: 500;
  }

  /* Control rows - make sure they're visible */
  .control-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    width: 100%;
    background-color: transparent;
  }

  .control-row label {
    flex: 0 0 40%;
    font-size: 13px;
    color: rgba(180, 210, 255, 0.8);
    display: block;
  }

  .control-row input[type="range"] {
    flex: 1;
    height: 4px;
    background: rgba(70, 100, 180, 0.3);
    border-radius: 2px;
    -webkit-appearance: none;
    margin: 0 10px;
    display: block;
  }

  /* Make sure value display is visible */
  .value-display {
    flex: 0 0 40px;
    text-align: right;
    font-size: 12px;
    color: rgba(160, 200, 255, 0.9);
    display: block;
  }

  /* Switch styling */
  .switch {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 20px;
    flex: 0 0 40px; /* Prevent stretching by setting a fixed flex basis */
  }

  .switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(70, 100, 180, 0.3);
    transition: 0.4s;
  }

  .slider:before {
    position: absolute;
    content: "";
    height: 16px;
    width: 16px;
    left: 3px;
    bottom: 2px;
    background-color: white;
    transition: 0.4s;
  }

  input:checked + .slider {
    background-color: rgba(100, 160, 255, 0.8);
  }

  input:checked + .slider:before {
    transform: translateX(18px);
  }

  .slider.round {
    border-radius: 20px;
  }

  .slider.round:before {
    border-radius: 50%;
  }

  .checkbox-label {
    text-align: center;
    flex: 0 0 60px;
    text-align: right;
    font-size: 12px;
    color: rgba(160, 200, 255, 0.9);
    white-space: nowrap;
    margin-left: 10px; /* Add spacing between switch and label */
  }

  /* About panel specific styles */
  .about-panel {
    text-align: center;
  }

  .logo-container {
    margin-bottom: 15px;
  }

  .logo-button {
    outline: none;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    transition: transform 0.2s ease;
  }

  .logo-button:hover,
  .logo-button:focus {
    transform: scale(1.05);
    outline: none;
  }

  .hollas-logo {
    max-width: 120px;
    height: auto;
  }

  .about-panel p {
    font-size: 13px;
    line-height: 1.5;
    margin-bottom: 10px;
    color: rgba(200, 220, 255, 0.8);
  }

  .about-panel a {
    color: rgba(100, 160, 255, 1);
    text-decoration: none;
  }

  .about-panel a:hover {
    text-decoration: underline;
  }

  .version {
    margin-top: 15px;
    font-size: 11px;
    color: rgba(150, 180, 255, 0.6);
  }

  /* Force box-sizing on all panel elements to prevent overflow */
  .control-panel,
  .control-panel * {
    box-sizing: border-box;
  }

  /* Responsive adjustments */
  @media (max-width: 480px) {
    .control-panel {
      width: calc(100% - 40px); /* Use calc to prevent horizontal scrolling */
      max-width: 330px; /* Updated from 320px */
    }

    .control-tabs {
      width: 100%;
      overflow-x: hidden;
    }

    .control-tabs button {
      padding: 8px 2px; /* Further reduced padding */
      font-size: 10px; /* Reduced font size further */
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .tab-content {
      padding: 15px 12px; /* Reduced horizontal padding */
      overflow-x: hidden;
    }

    .control-row {
      overflow: hidden;
    }

    .control-row label {
      font-size: 12px; /* Slightly smaller */
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .control-row input[type="range"] {
      max-width: 40%; /* Limit width */
    }

    .value-display {
      flex: 0 0 35px; /* Reduced from 40px */
      font-size: 11px; /* Smaller font */
    }

    .toggle-row {
      overflow: hidden;
    }

    .toggle-container {
      width: 100px; /* Set fixed width for toggle container */
    }

    .switch {
      width: 36px; /* Slightly smaller */
      height: 18px; /* Slightly smaller */
    }

    .slider:before {
      height: 14px; /* Smaller handle */
      width: 14px;
      left: 2px;
    }

    .checkbox-label {
      width: 50px; /* Reduced from 60px */
      font-size: 11px; /* Smaller font */
      margin-left: 6px; /* Less spacing */
    }
  }
</style>
